import { useState, useEffect } from "react";
import { useLanguage } from "@/contexts/LanguageContext";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Plus, Home, Info, HelpCircle, Mail, Navigation, FileText, Edit, Trash2, Image, Upload, X, Wallpaper } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { IconPicker } from "@/components/ui/IconPicker";
import { DynamicIcon } from "@/components/ui/DynamicIcon";
import { useToast } from "@/hooks/use-toast";
import { adminService } from "@/api/services/admin.service";
import { useAdminSiteSettings } from "@/hooks/api/use-site-settings";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  useHomepageContent,
  useHomepageFeatures,
  useCreateHomepageFeature,
  useUpdateHomepageFeature,
  useDeleteHomepageFeature,
  useHomepageStatistics,
  useCreateHomepageStatistic,
  useUpdateHomepageStatistic,
  useDeleteHomepageStatistic,
  useCreateOrUpdateHomepageContent,
  useAboutContent,
  useAboutValues,
  useCreateAboutValue,
  useUpdateAboutValue,
  useDeleteAboutValue,
  useCreateOrUpdateAboutContent,
  useHowItWorksContent,
  useHowItWorksSteps,
  useCreateHowItWorksStep,
  useUpdateHowItWorksStep,
  useDeleteHowItWorksStep,
  useCreateOrUpdateHowItWorksContent,
  useFAQs,
  useCreateFAQ,
  useUpdateFAQ,
  useDeleteFAQ,
  useContactContent,
  useCreateOrUpdateContactContent,
  useNavigationLinks,
  useCreateNavigationLink,
  useUpdateNavigationLink,
  useDeleteNavigationLink,
  useFooterContent,
  useCreateOrUpdateFooterContent,
} from "@/hooks/api/use-content";
import type { HomepageFeature, HomepageStatistic, AboutValue, HowItWorksStep, FAQ, NavigationLink } from "@/api/types";

const AdminContentManagement = () => {
  const { t, isRTL } = useLanguage();
  const { user } = useAuth();
  const isAdmin = user?.type === 'admin';

  // Debug: Log the current language and RTL state
  console.log('Language context - isRTL:', isRTL);

  if (!isAdmin) {
    return (
      <div className="container mx-auto px-4 py-8">
        <Card>
          <CardContent className="pt-6">
            <p className="text-center text-muted-foreground">
              {t('common.unauthorized') || 'غير مصرح لك بالوصول إلى هذه الصفحة'}
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8" dir={isRTL ? 'rtl' : 'ltr'}>
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-foreground mb-2">
          {t('content.title') || 'إدارة المحتوى'}
        </h1>
        <p className="text-muted-foreground">
          {t('content.subtitle') || 'إدارة محتوى المنصة من الصفحة الرئيسية، من نحن، كيف يعمل، الأسئلة الشائعة، التواصل، الروابط، والتذييل'}
        </p>
      </div>

      <Tabs defaultValue="homepage" className="space-y-6">
        <TabsList 
          className="flex w-full p-1 rounded-md bg-muted"
          dir={isRTL ? 'rtl' : 'ltr'}
          style={{
            direction: isRTL ? 'rtl' : 'ltr',
            flexDirection: isRTL ? 'row-reverse' : 'row',
          }}
        >
          {isRTL ? (
            <>
              <TabsTrigger value="footer" className="flex items-center justify-center gap-2 flex-1">
                <FileText className="w-4 h-4" />
                <span className="hidden sm:inline">{t('content.tabs.footer') || 'التذييل'}</span>
              </TabsTrigger>
              <TabsTrigger value="navigation" className="flex items-center justify-center gap-2 flex-1">
                <Navigation className="w-4 h-4" />
                <span className="hidden sm:inline">{t('content.tabs.navigation') || 'الروابط'}</span>
              </TabsTrigger>
              <TabsTrigger value="contact" className="flex items-center justify-center gap-2 flex-1">
                <Mail className="w-4 h-4" />
                <span className="hidden sm:inline">{t('content.tabs.contact') || 'التواصل'}</span>
              </TabsTrigger>
              <TabsTrigger value="faqs" className="flex items-center justify-center gap-2 flex-1">
                <HelpCircle className="w-4 h-4" />
                <span className="hidden sm:inline">{t('content.tabs.faqs') || 'الأسئلة الشائعة'}</span>
              </TabsTrigger>
              <TabsTrigger value="how-it-works" className="flex items-center justify-center gap-2 flex-1">
                <FileText className="w-4 h-4" />
                <span className="hidden sm:inline">{t('content.tabs.howItWorks') || 'كيف يعمل'}</span>
              </TabsTrigger>
              <TabsTrigger value="about" className="flex items-center justify-center gap-2 flex-1">
                <Info className="w-4 h-4" />
                <span className="hidden sm:inline">{t('content.tabs.about') || 'من نحن'}</span>
              </TabsTrigger>
              <TabsTrigger value="homepage" className="flex items-center justify-center gap-2 flex-1">
                <Home className="w-4 h-4" />
                <span className="hidden sm:inline">{t('content.tabs.homepage') || 'الصفحة الرئيسية'}</span>
              </TabsTrigger>
            </>
          ) : (
            <>
              <TabsTrigger value="homepage" className="flex items-center justify-center gap-2 flex-1">
                <Home className="w-4 h-4" />
                <span className="hidden sm:inline">{t('content.tabs.homepage') || 'الصفحة الرئيسية'}</span>
              </TabsTrigger>
              <TabsTrigger value="about" className="flex items-center justify-center gap-2 flex-1">
                <Info className="w-4 h-4" />
                <span className="hidden sm:inline">{t('content.tabs.about') || 'من نحن'}</span>
              </TabsTrigger>
              <TabsTrigger value="how-it-works" className="flex items-center justify-center gap-2 flex-1">
                <FileText className="w-4 h-4" />
                <span className="hidden sm:inline">{t('content.tabs.howItWorks') || 'كيف يعمل'}</span>
              </TabsTrigger>
              <TabsTrigger value="faqs" className="flex items-center justify-center gap-2 flex-1">
                <HelpCircle className="w-4 h-4" />
                <span className="hidden sm:inline">{t('content.tabs.faqs') || 'الأسئلة الشائعة'}</span>
              </TabsTrigger>
              <TabsTrigger value="contact" className="flex items-center justify-center gap-2 flex-1">
                <Mail className="w-4 h-4" />
                <span className="hidden sm:inline">{t('content.tabs.contact') || 'التواصل'}</span>
              </TabsTrigger>
              <TabsTrigger value="navigation" className="flex items-center justify-center gap-2 flex-1">
                <Navigation className="w-4 h-4" />
                <span className="hidden sm:inline">{t('content.tabs.navigation') || 'الروابط'}</span>
              </TabsTrigger>
              <TabsTrigger value="footer" className="flex items-center justify-center gap-2 flex-1">
                <FileText className="w-4 h-4" />
                <span className="hidden sm:inline">{t('content.tabs.footer') || 'التذييل'}</span>
              </TabsTrigger>
            </>
          )}
        </TabsList>

        {/* Homepage Tab */}
        <TabsContent value="homepage" className="space-y-6" dir={isRTL ? 'rtl' : 'ltr'}>
          <HomepageContentSection />
        </TabsContent>

        {/* About Tab */}
        <TabsContent value="about" className="space-y-6" dir={isRTL ? 'rtl' : 'ltr'}>
          <AboutContentSection />
        </TabsContent>

        {/* How It Works Tab */}
        <TabsContent value="how-it-works" className="space-y-6" dir={isRTL ? 'rtl' : 'ltr'}>
          <HowItWorksContentSection />
        </TabsContent>

        {/* FAQs Tab */}
        <TabsContent value="faqs" className="space-y-6" dir={isRTL ? 'rtl' : 'ltr'}>
          <FAQsContentSection />
        </TabsContent>

        {/* Contact Tab */}
        <TabsContent value="contact" className="space-y-6" dir={isRTL ? 'rtl' : 'ltr'}>
          <ContactContentSection />
        </TabsContent>

        {/* Navigation Tab */}
        <TabsContent value="navigation" className="space-y-6" dir={isRTL ? 'rtl' : 'ltr'}>
          <NavigationContentSection />
        </TabsContent>

        {/* Footer Tab */}
        <TabsContent value="footer" className="space-y-6" dir={isRTL ? 'rtl' : 'ltr'}>
          <FooterContentSection />
        </TabsContent>
      </Tabs>
    </div>
  );
};

// Homepage Content Section Component
const HomepageContentSection = () => {
  const { t, isRTL } = useLanguage();
  const { toast } = useToast();
  const [isContentDialogOpen, setIsContentDialogOpen] = useState(false);
  const [isFeatureDialogOpen, setIsFeatureDialogOpen] = useState(false);
  const [isStatisticDialogOpen, setIsStatisticDialogOpen] = useState(false);

  // Base URL for images
  const BASE_URL = 'https://maroon-grouse-638711.hostingersite.com/';

  // Debug: Log RTL state
  console.log('HomepageContentSection - isRTL:', isRTL, 'language:', isRTL ? 'Arabic' : 'English');
  const [editingFeature, setEditingFeature] = useState<HomepageFeature | null>(null);
  const [editingStatistic, setEditingStatistic] = useState<HomepageStatistic | null>(null);

  const { data: homepageContent, isLoading: contentLoading } = useHomepageContent();
  const { data: featuresData, isLoading: featuresLoading } = useHomepageFeatures();
  const { data: statisticsData, isLoading: statisticsLoading } = useHomepageStatistics();

  const {
    settings: siteSettings,
    uploadLogo,
    isUploadingLogo,
    deleteLogo,
    isDeletingLogo,
    isLoading: isLoadingSettings,
  } = useAdminSiteSettings();

  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);

  const [heroBgFile, setHeroBgFile] = useState<File | null>(null);
  const [heroBgPreview, setHeroBgPreview] = useState<string | null>(null);
  const [isUploadingHeroBg, setIsUploadingHeroBg] = useState(false);

  const createOrUpdateContent = useCreateOrUpdateHomepageContent();
  const createFeature = useCreateHomepageFeature();
  const updateFeature = useUpdateHomepageFeature();
  const deleteFeature = useDeleteHomepageFeature();
  const createStatistic = useCreateHomepageStatistic();
  const updateStatistic = useUpdateHomepageStatistic();
  const deleteStatistic = useDeleteHomepageStatistic();

  useEffect(() => {
    if (siteSettings?.logo) {
      // Add cache busting to ensure fresh image
      let url = siteSettings.logo;
      // Add base URL if it's a relative path
      if (url && !url.startsWith('http')) {
        url = `${BASE_URL}${url}`;
      }
      setLogoPreview(`${url}${url.includes('?') ? '&' : '?'}t=${Date.now()}`);
    } else {
      setLogoPreview(null);
    }
  }, [siteSettings?.logo]);

  useEffect(() => {
    const content = homepageContent?.data;
    if (content?.hero_background_image) {
      // Add cache busting to ensure fresh image
      let url = content.hero_background_image;
      // Add base URL if it's a relative path
      if (url && !url.startsWith('http')) {
        url = `${BASE_URL}${url}`;
      }
      setHeroBgPreview(`${url}${url.includes('?') ? '&' : '?'}t=${Date.now()}`);
    } else {
      setHeroBgPreview(null);
    }
  }, [homepageContent]);

  const handleLogoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setLogoFile(file);
    const reader = new FileReader();
    reader.onloadend = () => {
      setLogoPreview(reader.result as string);
    };
    reader.readAsDataURL(file);
  };

  const handleUploadLogo = () => {
    if (!logoFile) return;
    uploadLogo(logoFile);
    setLogoFile(null);
  };

  const handleHeroBgChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setHeroBgFile(file);
    const reader = new FileReader();
    reader.onloadend = () => {
      setHeroBgPreview(reader.result as string);
    };
    reader.readAsDataURL(file);
  };

  const handleUploadHeroBg = async () => {
    if (!heroBgFile) return;
    try {
      setIsUploadingHeroBg(true);
      await adminService.uploadHeroBackground(heroBgFile);
      toast({
        title: t('common.success') || 'Success',
        description: t('settings.heroBackground.uploadSuccess') || 'Hero background uploaded successfully',
      });
      setHeroBgFile(null);
    } catch {
      toast({
        title: t('common.error') || 'Error',
        description: t('settings.heroBackground.uploadError') || 'Failed to upload hero background',
        variant: 'destructive',
      });
    } finally {
      setIsUploadingHeroBg(false);
    }
  };

  const handleDeleteHeroBg = async () => {
    try {
      await adminService.deleteHeroBackground();
      setHeroBgPreview(null);
      toast({
        title: t('common.success') || 'Success',
        description: t('settings.heroBackground.deleteSuccess') || 'Hero background deleted successfully',
      });
    } catch {
      toast({
        title: t('common.error') || 'Error',
        description: t('settings.heroBackground.deleteError') || 'Failed to delete hero background',
        variant: 'destructive',
      });
    }
  };

  // Handle API response structure - API returns { success: true, data: [...] }
  const features = featuresData?.data || [];
  const statistics = statisticsData?.data || [];

  const handleOpenFeatureDialog = (feature?: HomepageFeature) => {
    setEditingFeature(feature || null);
    setIsFeatureDialogOpen(true);
  };

  const handleOpenStatisticDialog = (statistic?: HomepageStatistic) => {
    setEditingStatistic(statistic || null);
    setIsStatisticDialogOpen(true);
  };

  const handleDeleteFeature = (id: number) => {
    if (window.confirm(t('content.feature.confirmDelete') || 'هل أنت متأكد من حذف هذه الميزة؟')) {
      deleteFeature.mutate(id);
    }
  };

  const handleDeleteStatistic = (id: number) => {
    if (window.confirm(t('content.statistic.confirmDelete') || 'هل أنت متأكد من حذف هذه الإحصائية؟')) {
      deleteStatistic.mutate(id);
    }
  };

  return (
    <div className="space-y-6" dir={isRTL ? 'rtl' : 'ltr'}>
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>{t('content.homepage.title') || 'محتوى الصفحة الرئيسية'}</CardTitle>
              <CardDescription>
                {t('content.homepage.description') || 'إدارة محتوى الصفحة الرئيسية'}
              </CardDescription>
            </div>
            <Button onClick={() => setIsContentDialogOpen(true)}>
              <Plus className={`w-4 h-4 ${isRTL ? 'mr-2' : 'ml-2'}`} />
              {t('content.homepage.edit') || 'تعديل المحتوى'}
            </Button>
          </div>
        </CardHeader>
        <CardContent dir={isRTL ? 'rtl' : 'ltr'}>
          {contentLoading ? (
            <Skeleton className="h-32 w-full" />
          ) : homepageContent?.data ? (
            <div className="space-y-4">
              <div className={`flex flex-col ${isRTL ? 'text-right' : 'text-left'}`} style={{ textAlign: isRTL ? 'right' : 'left', direction: isRTL ? 'rtl' : 'ltr' }}>
                <strong className="text-sm font-semibold mb-3">{t('content.homepage.heroTitle') || 'العنوان الرئيسي'}:</strong>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div className="p-3 bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700">
                    <span className="text-xs font-medium text-slate-500 dark:text-slate-400">🇸🇦 English</span>
                    <p className="mt-1 text-sm font-medium" style={{ color: homepageContent.data.hero_title_color || '#000000' }}>
                      {typeof homepageContent.data.hero_title === 'string' 
                        ? homepageContent.data.hero_title
                        : (typeof homepageContent.data.hero_title === 'object' && homepageContent.data.hero_title?.en 
                          ? homepageContent.data.hero_title.en 
                          : (homepageContent.data.hero_title?.ar || '-'))}
                    </p>
                  </div>
                  <div className="p-3 bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700">
                    <span className="text-xs font-medium text-slate-500 dark:text-slate-400">🇸🇦 Arabic</span>
                    <p className="mt-1 text-sm font-medium" style={{ color: homepageContent.data.hero_title_color || '#000000' }}>
                      {typeof homepageContent.data.hero_title === 'string'
                        ? homepageContent.data.hero_title
                        : (typeof homepageContent.data.hero_title === 'object' && homepageContent.data.hero_title?.ar 
                          ? homepageContent.data.hero_title.ar 
                          : (homepageContent.data.hero_title?.en || '-'))}
                    </p>
                  </div>
                </div>
              </div>
              <div className={`flex flex-col ${isRTL ? 'text-right' : 'text-left'}`} style={{ textAlign: isRTL ? 'right' : 'left', direction: isRTL ? 'rtl' : 'ltr' }}>
                <strong className="text-sm font-semibold mb-3">{t('content.homepage.heroDescription') || 'الوصف الرئيسي'}:</strong>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div className="p-3 bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700">
                    <span className="text-xs font-medium text-slate-500 dark:text-slate-400">🇸🇦 English</span>
                    <p className="mt-1 text-sm text-slate-700 dark:text-slate-300">
                      {typeof homepageContent.data.hero_description === 'string'
                        ? homepageContent.data.hero_description
                        : (typeof homepageContent.data.hero_description === 'object' && homepageContent.data.hero_description?.en 
                          ? homepageContent.data.hero_description.en 
                          : (homepageContent.data.hero_description?.ar || '-'))}
                    </p>
                  </div>
                  <div className="p-3 bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700">
                    <span className="text-xs font-medium text-slate-500 dark:text-slate-400">🇸🇦 Arabic</span>
                    <p className="mt-1 text-sm text-slate-700 dark:text-slate-300">
                      {typeof homepageContent.data.hero_description === 'string'
                        ? homepageContent.data.hero_description
                        : (typeof homepageContent.data.hero_description === 'object' && homepageContent.data.hero_description?.ar 
                          ? homepageContent.data.hero_description.ar 
                          : (homepageContent.data.hero_description?.en || '-'))}
                    </p>
                  </div>
                </div>
              </div>
              <div className={`flex flex-col ${isRTL ? 'text-right' : 'text-left'}`} style={{ textAlign: isRTL ? 'right' : 'left', direction: isRTL ? 'rtl' : 'ltr' }}>
                <strong className="text-sm font-semibold mb-3">{t('content.homepage.heroCtaText') || 'نص زر الدعوة'}:</strong>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div className="p-3 bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700">
                    <span className="text-xs font-medium text-slate-500 dark:text-slate-400">🇸🇦 English</span>
                    <p className="mt-1 text-sm font-medium text-slate-900 dark:text-slate-100">
                      {typeof homepageContent.data.hero_cta_text === 'string'
                        ? homepageContent.data.hero_cta_text
                        : (typeof homepageContent.data.hero_cta_text === 'object' && homepageContent.data.hero_cta_text?.en 
                          ? homepageContent.data.hero_cta_text.en 
                          : (homepageContent.data.hero_cta_text?.ar || '-'))}
                    </p>
                  </div>
                  <div className="p-3 bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700">
                    <span className="text-xs font-medium text-slate-500 dark:text-slate-400">🇸🇦 Arabic</span>
                    <p className="mt-1 text-sm font-medium text-slate-900 dark:text-slate-100">
                      {typeof homepageContent.data.hero_cta_text === 'string'
                        ? homepageContent.data.hero_cta_text
                        : (typeof homepageContent.data.hero_cta_text === 'object' && homepageContent.data.hero_cta_text?.ar 
                          ? homepageContent.data.hero_cta_text.ar 
                          : (homepageContent.data.hero_cta_text?.en || '-'))}
                    </p>
                  </div>
                </div>
              </div>
              <div className={`flex flex-col ${isRTL ? 'text-right' : 'text-left'}`} style={{ textAlign: isRTL ? 'right' : 'left', direction: isRTL ? 'rtl' : 'ltr' }}>
                <strong className="text-sm font-semibold mb-3">{t('content.homepage.heroCtaUrl') || 'رابط زر الدعوة'}:</strong>
                <div className="p-3 bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700">
                  <p className="text-sm text-slate-700 dark:text-slate-300">
                    {homepageContent.data.hero_cta_url || '-'}
                  </p>
                </div>
              </div>
              <div className={`flex flex-col ${isRTL ? 'text-right' : 'text-left'}`} style={{ textAlign: isRTL ? 'right' : 'left', direction: isRTL ? 'rtl' : 'ltr' }}>
                <strong className="text-sm font-semibold mb-3">{t('content.homepage.sectionSubtitle') || 'عنوان القسم الفرعي'}:</strong>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div className="p-3 bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700">
                    <span className="text-xs font-medium text-slate-500 dark:text-slate-400">🇸🇦 English</span>
                    <p className="mt-1 text-sm font-medium text-slate-900 dark:text-slate-100">
                      {typeof homepageContent.data.section_subtitle === 'string'
                        ? homepageContent.data.section_subtitle
                        : (typeof homepageContent.data.section_subtitle === 'object' && homepageContent.data.section_subtitle?.en 
                          ? homepageContent.data.section_subtitle.en 
                          : (homepageContent.data.section_subtitle?.ar || '-'))}
                    </p>
                  </div>
                  <div className="p-3 bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700">
                    <span className="text-xs font-medium text-slate-500 dark:text-slate-400">🇸🇦 Arabic</span>
                    <p className="mt-1 text-sm font-medium text-slate-900 dark:text-slate-100">
                      {typeof homepageContent.data.section_subtitle === 'string'
                        ? homepageContent.data.section_subtitle
                        : (typeof homepageContent.data.section_subtitle === 'object' && homepageContent.data.section_subtitle?.ar 
                          ? homepageContent.data.section_subtitle.ar 
                          : (homepageContent.data.section_subtitle?.en || '-'))}
                    </p>
                  </div>
                </div>
              </div>
              {homepageContent.data.hero_background_image && (
                <div className={`flex flex-col ${isRTL ? 'text-right' : 'text-left'}`} style={{ textAlign: isRTL ? 'right' : 'left', direction: isRTL ? 'rtl' : 'ltr' }}>
                  <strong className="text-sm font-semibold mb-3">{t('content.homepage.heroBackgroundImage') || 'صورة الخلفية الرئيسية'}:</strong>
                  <div className="p-3 bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700">
                    <p className="text-sm text-slate-700 dark:text-slate-300">
                      {homepageContent.data.hero_background_image}
                    </p>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <p className="text-muted-foreground">
              {t('content.homepage.noContent') || 'لا يوجد محتوى. اضغط على تعديل المحتوى لإضافة محتوى جديد.'}
            </p>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Image className="w-5 h-5" />
            {t('settings.siteLogo.title') || 'Site Logo'}
          </CardTitle>
          <CardDescription>
            {t('settings.siteLogo.description') || 'Upload and manage your site logo'}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-start gap-6">
            <div className="flex-shrink-0">
              {isLoadingSettings ? (
                <div className="h-20 w-32 animate-pulse bg-gradient-to-r from-slate-200 via-slate-100 to-slate-200 bg-[length:200%_100%] rounded-lg" 
                     style={{ animation: 'shimmer 1.5s infinite' }} />
              ) : logoPreview ? (
                <div className="relative">
                  <img
                    src={logoPreview}
                    alt={t('settings.siteLogo.alt') || 'Site Logo'}
                    className="h-20 w-auto object-contain border rounded-lg p-2 bg-white"
                    onError={(e) => {
                      console.error('Logo failed to load:', logoPreview);
                      e.currentTarget.style.display = 'none';
                    }}
                  />
                  <button
                    onClick={() => deleteLogo()}
                    disabled={isDeletingLogo}
                    className="absolute -top-2 -right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              ) : (
                <div className="h-20 w-32 border-2 border-dashed rounded-lg flex items-center justify-center bg-gray-50">
                  <Image className="h-8 w-8 text-gray-400" />
                </div>
              )}
            </div>

            <div className="flex-1 space-y-3">
              <div className="flex items-center gap-3">
                <Input
                  type="file"
                  accept="image/*"
                  onChange={handleLogoChange}
                  className="flex-1"
                />
                {logoFile && (
                  <Button
                    onClick={handleUploadLogo}
                    disabled={isUploadingLogo}
                    className="flex items-center gap-2"
                  >
                    <Upload className="h-4 w-4" />
                    {isUploadingLogo ? (t('settings.uploading') || 'Uploading...') : (t('settings.upload') || 'Upload')}
                  </Button>
                )}
              </div>
              <p className="text-sm text-muted-foreground">
                {t('settings.siteLogo.recommended') || 'Recommended: PNG or SVG with transparent background. Max 2MB.'}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Wallpaper className="w-5 h-5" />
            {t('settings.heroBackground.title') || 'Hero Background'}
          </CardTitle>
          <CardDescription>
            {t('settings.heroBackground.description') || 'Upload background image for the homepage hero section'}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-start gap-6">
            <div className="flex-shrink-0">
              {contentLoading ? (
                <div className="h-24 w-40 animate-pulse bg-gradient-to-r from-slate-200 via-slate-100 to-slate-200 bg-[length:200%_100%] rounded-lg" 
                     style={{ animation: 'shimmer 1.5s infinite' }} />
              ) : heroBgPreview ? (
                <div className="relative">
                  <img
                    src={heroBgPreview}
                    alt={t('settings.heroBackground.alt') || 'Hero Background'}
                    className="h-24 w-40 object-cover border rounded-lg"
                    onError={(e) => {
                      console.error('Hero background failed to load:', heroBgPreview);
                      e.currentTarget.style.display = 'none';
                    }}
                  />
                  <button
                    onClick={handleDeleteHeroBg}
                    className="absolute -top-2 -right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              ) : (
                <div className="h-24 w-40 border-2 border-dashed rounded-lg flex items-center justify-center bg-gray-50">
                  <Wallpaper className="h-8 w-8 text-gray-400" />
                </div>
              )}
            </div>

            <div className="flex-1 space-y-3">
              <div className="flex items-center gap-3">
                <Input
                  type="file"
                  accept="image/*"
                  onChange={handleHeroBgChange}
                  className="flex-1"
                />
                {heroBgFile && (
                  <Button
                    onClick={handleUploadHeroBg}
                    disabled={isUploadingHeroBg}
                    className="flex items-center gap-2"
                  >
                    <Upload className="h-4 w-4" />
                    {isUploadingHeroBg ? (t('settings.uploading') || 'Uploading...') : (t('settings.upload') || 'Upload')}
                  </Button>
                )}
              </div>
              <p className="text-sm text-muted-foreground">
                {t('settings.heroBackground.recommended') || 'Recommended: High-quality image (1920x1080 or higher). Max 5MB. Supports PNG, JPG, WebP.'}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>{t('content.homepage.features') || 'الميزات'}</CardTitle>
              <CardDescription>
                {t('content.homepage.featuresDescription') || 'إدارة ميزات الصفحة الرئيسية'}
              </CardDescription>
            </div>
            <Button onClick={() => handleOpenFeatureDialog()}>
              <Plus className={`w-4 h-4 ${isRTL ? 'mr-2' : 'ml-2'}`} />
              {t('content.homepage.addFeature') || 'إضافة ميزة'}
            </Button>
          </div>
        </CardHeader>
        <CardContent dir={isRTL ? 'rtl' : 'ltr'}>
          {featuresLoading ? (
            <div className="space-y-4">
              <Skeleton className="h-24 w-full" />
              <Skeleton className="h-24 w-full" />
            </div>
          ) : features.length > 0 ? (
            <div className="space-y-4">
              {features.map((feature) => (
                <div key={feature.id} className="p-4 border rounded-lg space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <DynamicIcon iconName={feature.icon || 'fa:Circle'} className="w-5 h-5 text-primary" />
                      <span className="text-xs text-slate-500">{feature.icon || '-'}</span>
                      <span className="text-xs text-slate-500">{t('content.feature.order') || 'الترتيب'}: {feature.order}</span>
                    </div>
                    <div className="flex gap-2">
                      <Button variant="outline" size="sm" onClick={() => handleOpenFeatureDialog(feature)}>
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button variant="destructive" size="sm" onClick={() => handleDeleteFeature(feature.id)}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                  <div className={`flex flex-col ${isRTL ? 'text-right' : 'text-left'}`} style={{ textAlign: isRTL ? 'right' : 'left', direction: isRTL ? 'rtl' : 'ltr' }}>
                    <strong className="text-xs font-medium text-slate-500 mb-2">{t('content.feature.title') || 'العنوان'}:</strong>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                      <div className="p-2 bg-white dark:bg-slate-800 rounded border border-slate-200 dark:border-slate-700">
                        <span className="text-xs text-slate-400">🇬🇧 EN</span>
                        <p className="text-sm font-medium">
                          {typeof feature.title === 'string' 
                            ? feature.title 
                            : (typeof feature.title === 'object' && feature.title?.en 
                              ? feature.title.en 
                              : (feature.title?.ar || '-'))}
                        </p>
                      </div>
                      <div className="p-2 bg-white dark:bg-slate-800 rounded border border-slate-200 dark:border-slate-700">
                        <span className="text-xs text-slate-400">🇸🇦 AR</span>
                        <p className="text-sm font-medium">
                          {typeof feature.title === 'string' 
                            ? feature.title 
                            : (typeof feature.title === 'object' && feature.title?.ar 
                              ? feature.title.ar 
                              : (feature.title?.en || '-'))}
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className={`flex flex-col ${isRTL ? 'text-right' : 'text-left'}`} style={{ textAlign: isRTL ? 'right' : 'left', direction: isRTL ? 'rtl' : 'ltr' }}>
                    <strong className="text-xs font-medium text-slate-500 mb-2">{t('content.feature.description') || 'الوصف'}:</strong>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                      <div className="p-2 bg-white dark:bg-slate-800 rounded border border-slate-200 dark:border-slate-700">
                        <span className="text-xs text-slate-400">🇬🇧 EN</span>
                        <p className="text-sm text-slate-600 dark:text-slate-300">
                          {typeof feature.description === 'string'
                            ? feature.description
                            : (typeof feature.description === 'object' && feature.description?.en 
                              ? feature.description.en 
                              : (feature.description?.ar || '-'))}
                        </p>
                      </div>
                      <div className="p-2 bg-white dark:bg-slate-800 rounded border border-slate-200 dark:border-slate-700">
                        <span className="text-xs text-slate-400">🇸🇦 AR</span>
                        <p className="text-sm text-slate-600 dark:text-slate-300">
                          {typeof feature.description === 'string'
                            ? feature.description
                            : (typeof feature.description === 'object' && feature.description?.ar 
                              ? feature.description.ar 
                              : (feature.description?.en || '-'))}
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-4 text-xs text-slate-500">
                    <span>{t('content.feature.status') || 'الحالة'}: {feature.is_active ? '✅' : '❌'}</span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-muted-foreground">
              {t('content.homepage.noFeatures') || 'لا توجد ميزات. اضغط على إضافة ميزة لإضافة ميزة جديدة.'}
            </p>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>{t('content.homepage.statistics') || 'الإحصائيات'}</CardTitle>
              <CardDescription>
                {t('content.homepage.statisticsDescription') || 'إدارة إحصائيات الصفحة الرئيسية'}
              </CardDescription>
            </div>
            <Button onClick={() => handleOpenStatisticDialog()}>
              <Plus className={`w-4 h-4 ${isRTL ? 'mr-2' : 'ml-2'}`} />
              {t('content.homepage.addStatistic') || 'إضافة إحصائية'}
            </Button>
          </div>
        </CardHeader>
        <CardContent dir={isRTL ? 'rtl' : 'ltr'}>
          {statisticsLoading ? (
            <div className="space-y-4">
              <Skeleton className="h-24 w-full" />
              <Skeleton className="h-24 w-full" />
            </div>
          ) : statistics.length > 0 ? (
            <div className="space-y-4">
              {statistics.map((statistic) => (
                <div key={statistic.id} className="p-4 border rounded-lg space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-slate-500">{t('content.statistic.order') || 'الترتيب'}: {statistic.order}</span>
                    </div>
                    <div className="flex gap-2">
                      <Button variant="outline" size="sm" onClick={() => handleOpenStatisticDialog(statistic)}>
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button variant="destructive" size="sm" onClick={() => handleDeleteStatistic(statistic.id)}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                  <div className={`flex flex-col ${isRTL ? 'text-right' : 'text-left'}`} style={{ textAlign: isRTL ? 'right' : 'left', direction: isRTL ? 'rtl' : 'ltr' }}>
                    <strong className="text-xs font-medium text-slate-500 mb-2">{t('content.statistic.number') || 'الرقم'}:</strong>
                    <div className="p-2 bg-white dark:bg-slate-800 rounded border border-slate-200 dark:border-slate-700">
                      <span className="text-2xl font-bold text-primary">{statistic.number || '-'}</span>
                    </div>
                  </div>
                  <div className={`flex flex-col ${isRTL ? 'text-right' : 'text-left'}`} style={{ textAlign: isRTL ? 'right' : 'left', direction: isRTL ? 'rtl' : 'ltr' }}>
                    <strong className="text-xs font-medium text-slate-500 mb-2">{t('content.statistic.label') || 'التسمية'}:</strong>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                      <div className="p-2 bg-white dark:bg-slate-800 rounded border border-slate-200 dark:border-slate-700">
                        <span className="text-xs text-slate-400">🇬🇧 EN</span>
                        <p className="text-sm font-medium">
                          {typeof statistic.label === 'string'
                            ? statistic.label
                            : (typeof statistic.label === 'object' && statistic.label?.en 
                              ? statistic.label.en 
                              : (statistic.label?.ar || '-'))}
                        </p>
                      </div>
                      <div className="p-2 bg-white dark:bg-slate-800 rounded border border-slate-200 dark:border-slate-700">
                        <span className="text-xs text-slate-400">🇸🇦 AR</span>
                        <p className="text-sm font-medium">
                          {typeof statistic.label === 'string'
                            ? statistic.label
                            : (typeof statistic.label === 'object' && statistic.label?.ar 
                              ? statistic.label.ar 
                              : (statistic.label?.en || '-'))}
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-4 text-xs text-slate-500">
                    <span>{t('content.statistic.status') || 'الحالة'}: {statistic.is_active ? '✅' : '❌'}</span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-muted-foreground">
              {t('content.homepage.noStatistics') || 'لا توجد إحصائيات. اضغط على إضافة إحصائية لإضافة إحصائية جديدة.'}
            </p>
          )}
        </CardContent>
      </Card>

      {/* Homepage Content Dialog */}
      <HomepageContentDialog 
        isOpen={isContentDialogOpen} 
        onClose={() => setIsContentDialogOpen(false)}
        content={homepageContent?.data}
        onSubmit={createOrUpdateContent.mutate}
      />

      {/* Feature Dialog */}
      <FeatureDialog
        isOpen={isFeatureDialogOpen}
        onClose={() => {
          setIsFeatureDialogOpen(false);
          setEditingFeature(null);
        }}
        feature={editingFeature}
        onCreate={createFeature.mutate}
        onUpdate={updateFeature.mutate}
      />

      {/* Statistic Dialog */}
      <StatisticDialog
        isOpen={isStatisticDialogOpen}
        onClose={() => {
          setIsStatisticDialogOpen(false);
          setEditingStatistic(null);
        }}
        statistic={editingStatistic}
        onCreate={createStatistic.mutate}
        onUpdate={updateStatistic.mutate}
      />
    </div>
  );
};

// About Content Section Component
const AboutContentSection = () => {
  const { t, isRTL } = useLanguage();
  const [isContentDialogOpen, setIsContentDialogOpen] = useState(false);
  const [isValueDialogOpen, setIsValueDialogOpen] = useState(false);
  const [editingValue, setEditingValue] = useState<AboutValue | null>(null);

  const { data: aboutContent, isLoading: contentLoading } = useAboutContent();
  const { data: valuesData, isLoading: valuesLoading } = useAboutValues();

  const createOrUpdateContent = useCreateOrUpdateAboutContent();
  const createValue = useCreateAboutValue();
  const updateValue = useUpdateAboutValue();
  const deleteValue = useDeleteAboutValue();

  const values = valuesData?.data || [];

  const handleOpenValueDialog = (value?: AboutValue) => {
    setEditingValue(value || null);
    setIsValueDialogOpen(true);
  };

  const handleDeleteValue = (id: number) => {
    if (window.confirm(t('content.value.confirmDelete') || 'هل أنت متأكد من حذف هذه القيمة؟')) {
      deleteValue.mutate(id);
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>{t('content.about.title') || 'محتوى صفحة من نحن'}</CardTitle>
              <CardDescription>
                {t('content.about.description') || 'إدارة محتوى صفحة من نحن'}
              </CardDescription>
            </div>
            <Button onClick={() => setIsContentDialogOpen(true)}>
              <Plus className={`w-4 h-4 ${isRTL ? 'mr-2' : 'ml-2'}`} />
              {t('content.about.edit') || 'تعديل المحتوى'}
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {contentLoading ? (
            <Skeleton className="h-32 w-full" />
          ) : aboutContent?.data ? (
            <div className="space-y-4">
              {/* Hero Title */}
              <div className={`flex flex-col ${isRTL ? 'text-right' : 'text-left'}`} style={{ textAlign: isRTL ? 'right' : 'left', direction: isRTL ? 'rtl' : 'ltr' }}>
                <strong className="text-sm font-semibold mb-3">{t('content.about.heroTitle') || 'العنوان الرئيسي'}:</strong>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div className="p-3 bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700">
                    <span className="text-xs font-medium text-slate-500 dark:text-slate-400">🇬🇧 English</span>
                    <p className="mt-1 text-sm font-medium text-slate-900 dark:text-slate-100">
                      {typeof aboutContent.data.hero_title === 'string'
                        ? aboutContent.data.hero_title
                        : (typeof aboutContent.data.hero_title === 'object' && aboutContent.data.hero_title?.en 
                          ? aboutContent.data.hero_title.en 
                          : (aboutContent.data.hero_title?.ar || '-'))}
                    </p>
                  </div>
                  <div className="p-3 bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700">
                    <span className="text-xs font-medium text-slate-500 dark:text-slate-400">🇸🇦 Arabic</span>
                    <p className="mt-1 text-sm font-medium text-slate-900 dark:text-slate-100">
                      {typeof aboutContent.data.hero_title === 'string'
                        ? aboutContent.data.hero_title
                        : (typeof aboutContent.data.hero_title === 'object' && aboutContent.data.hero_title?.ar 
                          ? aboutContent.data.hero_title.ar 
                          : (aboutContent.data.hero_title?.en || '-'))}
                    </p>
                  </div>
                </div>
              </div>
              {/* Hero Subtitle */}
              <div className={`flex flex-col ${isRTL ? 'text-right' : 'text-left'}`} style={{ textAlign: isRTL ? 'right' : 'left', direction: isRTL ? 'rtl' : 'ltr' }}>
                <strong className="text-sm font-semibold mb-3">{t('content.about.heroSubtitle') || 'الوصف الرئيسي'}:</strong>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div className="p-3 bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700">
                    <span className="text-xs font-medium text-slate-500 dark:text-slate-400">🇬🇧 English</span>
                    <p className="mt-1 text-sm text-slate-700 dark:text-slate-300">
                      {typeof aboutContent.data.hero_subtitle === 'string'
                        ? aboutContent.data.hero_subtitle
                        : (typeof aboutContent.data.hero_subtitle === 'object' && aboutContent.data.hero_subtitle?.en 
                          ? aboutContent.data.hero_subtitle.en 
                          : (aboutContent.data.hero_subtitle?.ar || '-'))}
                    </p>
                  </div>
                  <div className="p-3 bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700">
                    <span className="text-xs font-medium text-slate-500 dark:text-slate-400">🇸🇦 Arabic</span>
                    <p className="mt-1 text-sm text-slate-700 dark:text-slate-300">
                      {typeof aboutContent.data.hero_subtitle === 'string'
                        ? aboutContent.data.hero_subtitle
                        : (typeof aboutContent.data.hero_subtitle === 'object' && aboutContent.data.hero_subtitle?.ar 
                          ? aboutContent.data.hero_subtitle.ar 
                          : (aboutContent.data.hero_subtitle?.en || '-'))}
                    </p>
                  </div>
                </div>
              </div>
              {/* Vision Title */}
              <div className={`flex flex-col ${isRTL ? 'text-right' : 'text-left'}`} style={{ textAlign: isRTL ? 'right' : 'left', direction: isRTL ? 'rtl' : 'ltr' }}>
                <strong className="text-sm font-semibold mb-3">{t('content.about.visionTitle') || 'عنوان الرؤية'}:</strong>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div className="p-3 bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700">
                    <span className="text-xs font-medium text-slate-500 dark:text-slate-400">🇬🇧 English</span>
                    <p className="mt-1 text-sm font-medium text-slate-900 dark:text-slate-100">
                      {typeof aboutContent.data.vision_title === 'string'
                        ? aboutContent.data.vision_title
                        : (typeof aboutContent.data.vision_title === 'object' && aboutContent.data.vision_title?.en 
                          ? aboutContent.data.vision_title.en 
                          : (aboutContent.data.vision_title?.ar || '-'))}
                    </p>
                  </div>
                  <div className="p-3 bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700">
                    <span className="text-xs font-medium text-slate-500 dark:text-slate-400">🇸🇦 Arabic</span>
                    <p className="mt-1 text-sm font-medium text-slate-900 dark:text-slate-100">
                      {typeof aboutContent.data.vision_title === 'string'
                        ? aboutContent.data.vision_title
                        : (typeof aboutContent.data.vision_title === 'object' && aboutContent.data.vision_title?.ar 
                          ? aboutContent.data.vision_title.ar 
                          : (aboutContent.data.vision_title?.en || '-'))}
                    </p>
                  </div>
                </div>
              </div>
              {/* Vision Content */}
              <div className={`flex flex-col ${isRTL ? 'text-right' : 'text-left'}`} style={{ textAlign: isRTL ? 'right' : 'left', direction: isRTL ? 'rtl' : 'ltr' }}>
                <strong className="text-sm font-semibold mb-3">{t('content.about.visionContentEn') || 'محتوى الرؤية'}:</strong>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div className="p-3 bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700">
                    <span className="text-xs font-medium text-slate-500 dark:text-slate-400">🇬🇧 English</span>
                    <p className="mt-1 text-sm text-slate-700 dark:text-slate-300">
                      {typeof aboutContent.data.vision_content === 'string'
                        ? aboutContent.data.vision_content
                        : (typeof aboutContent.data.vision_content === 'object' && aboutContent.data.vision_content?.en 
                          ? aboutContent.data.vision_content.en 
                          : (aboutContent.data.vision_content?.ar || '-'))}
                    </p>
                  </div>
                  <div className="p-3 bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700">
                    <span className="text-xs font-medium text-slate-500 dark:text-slate-400">🇸🇦 Arabic</span>
                    <p className="mt-1 text-sm text-slate-700 dark:text-slate-300">
                      {typeof aboutContent.data.vision_content === 'string'
                        ? aboutContent.data.vision_content
                        : (typeof aboutContent.data.vision_content === 'object' && aboutContent.data.vision_content?.ar 
                          ? aboutContent.data.vision_content.ar 
                          : (aboutContent.data.vision_content?.en || '-'))}
                    </p>
                  </div>
                </div>
              </div>
              {/* Vision Icon */}
              <div className={`flex flex-col ${isRTL ? 'text-right' : 'text-left'}`} style={{ textAlign: isRTL ? 'right' : 'left', direction: isRTL ? 'rtl' : 'ltr' }}>
                <strong className="text-sm font-semibold mb-3">{t('content.about.visionIcon') || 'أيقونة الرؤية'}:</strong>
                <div className="p-3 bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 flex items-center gap-2">
                  <DynamicIcon iconName={aboutContent.data.vision_icon || 'fa:Circle'} className="w-5 h-5 text-primary" />
                  <span className="text-sm text-slate-700 dark:text-slate-300">{aboutContent.data.vision_icon || '-'}</span>
                </div>
              </div>
              {/* Mission Title */}
              <div className={`flex flex-col ${isRTL ? 'text-right' : 'text-left'}`} style={{ textAlign: isRTL ? 'right' : 'left', direction: isRTL ? 'rtl' : 'ltr' }}>
                <strong className="text-sm font-semibold mb-3">{t('content.about.missionTitle') || 'عنوان المهمة'}:</strong>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div className="p-3 bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700">
                    <span className="text-xs font-medium text-slate-500 dark:text-slate-400">🇬🇧 English</span>
                    <p className="mt-1 text-sm font-medium text-slate-900 dark:text-slate-100">
                      {typeof aboutContent.data.mission_title === 'string'
                        ? aboutContent.data.mission_title
                        : (typeof aboutContent.data.mission_title === 'object' && aboutContent.data.mission_title?.en 
                          ? aboutContent.data.mission_title.en 
                          : (aboutContent.data.mission_title?.ar || '-'))}
                    </p>
                  </div>
                  <div className="p-3 bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700">
                    <span className="text-xs font-medium text-slate-500 dark:text-slate-400">🇸🇦 Arabic</span>
                    <p className="mt-1 text-sm font-medium text-slate-900 dark:text-slate-100">
                      {typeof aboutContent.data.mission_title === 'string'
                        ? aboutContent.data.mission_title
                        : (typeof aboutContent.data.mission_title === 'object' && aboutContent.data.mission_title?.ar 
                          ? aboutContent.data.mission_title.ar 
                          : (aboutContent.data.mission_title?.en || '-'))}
                    </p>
                  </div>
                </div>
              </div>
              {/* Mission Content */}
              <div className={`flex flex-col ${isRTL ? 'text-right' : 'text-left'}`} style={{ textAlign: isRTL ? 'right' : 'left', direction: isRTL ? 'rtl' : 'ltr' }}>
                <strong className="text-sm font-semibold mb-3">{t('content.about.missionContentEn') || 'محتوى المهمة'}:</strong>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div className="p-3 bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700">
                    <span className="text-xs font-medium text-slate-500 dark:text-slate-400">🇬🇧 English</span>
                    <p className="mt-1 text-sm text-slate-700 dark:text-slate-300">
                      {typeof aboutContent.data.mission_content === 'string'
                        ? aboutContent.data.mission_content
                        : (typeof aboutContent.data.mission_content === 'object' && aboutContent.data.mission_content?.en 
                          ? aboutContent.data.mission_content.en 
                          : (aboutContent.data.mission_content?.ar || '-'))}
                    </p>
                  </div>
                  <div className="p-3 bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700">
                    <span className="text-xs font-medium text-slate-500 dark:text-slate-400">🇸🇦 Arabic</span>
                    <p className="mt-1 text-sm text-slate-700 dark:text-slate-300">
                      {typeof aboutContent.data.mission_content === 'string'
                        ? aboutContent.data.mission_content
                        : (typeof aboutContent.data.mission_content === 'object' && aboutContent.data.mission_content?.ar 
                          ? aboutContent.data.mission_content.ar 
                          : (aboutContent.data.mission_content?.en || '-'))}
                    </p>
                  </div>
                </div>
              </div>
              {/* Mission Icon */}
              <div className={`flex flex-col ${isRTL ? 'text-right' : 'text-left'}`} style={{ textAlign: isRTL ? 'right' : 'left', direction: isRTL ? 'rtl' : 'ltr' }}>
                <strong className="text-sm font-semibold mb-3">{t('content.about.missionIcon') || 'أيقونة المهمة'}:</strong>
                <div className="p-3 bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 flex items-center gap-2">
                  <DynamicIcon iconName={aboutContent.data.mission_icon || 'fa:Circle'} className="w-5 h-5 text-primary" />
                  <span className="text-sm text-slate-700 dark:text-slate-300">{aboutContent.data.mission_icon || '-'}</span>
                </div>
              </div>
              {/* Values Section Title */}
              <div className={`flex flex-col ${isRTL ? 'text-right' : 'text-left'}`} style={{ textAlign: isRTL ? 'right' : 'left', direction: isRTL ? 'rtl' : 'ltr' }}>
                <strong className="text-sm font-semibold mb-3">{t('content.about.valuesSectionTitleEn') || 'عنوان قسم القيم'}:</strong>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div className="p-3 bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700">
                    <span className="text-xs font-medium text-slate-500 dark:text-slate-400">🇬🇧 English</span>
                    <p className="mt-1 text-sm font-medium text-slate-900 dark:text-slate-100">
                      {typeof aboutContent.data.values_section_title === 'string'
                        ? aboutContent.data.values_section_title
                        : (typeof aboutContent.data.values_section_title === 'object' && aboutContent.data.values_section_title?.en 
                          ? aboutContent.data.values_section_title.en 
                          : (aboutContent.data.values_section_title?.ar || '-'))}
                    </p>
                  </div>
                  <div className="p-3 bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700">
                    <span className="text-xs font-medium text-slate-500 dark:text-slate-400">🇸🇦 Arabic</span>
                    <p className="mt-1 text-sm font-medium text-slate-900 dark:text-slate-100">
                      {typeof aboutContent.data.values_section_title === 'string'
                        ? aboutContent.data.values_section_title
                        : (typeof aboutContent.data.values_section_title === 'object' && aboutContent.data.values_section_title?.ar 
                          ? aboutContent.data.values_section_title.ar 
                          : (aboutContent.data.values_section_title?.en || '-'))}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <p className="text-muted-foreground">
              {t('content.about.noContent') || 'لا يوجد محتوى. اضغط على تعديل المحتوى لإضافة محتوى جديد.'}
            </p>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>{t('content.about.values') || 'القيم'}</CardTitle>
              <CardDescription>
                {t('content.about.valuesDescription') || 'إدارة قيم الشركة'}
              </CardDescription>
            </div>
            <Button onClick={() => handleOpenValueDialog()}>
              <Plus className={`w-4 h-4 ${isRTL ? 'mr-2' : 'ml-2'}`} />
              {t('content.about.addValue') || 'إضافة قيمة'}
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {valuesLoading ? (
            <div className="space-y-4">
              <Skeleton className="h-24 w-full" />
              <Skeleton className="h-24 w-full" />
            </div>
          ) : values.length > 0 ? (
            <div className="space-y-4">
              {values.map((value) => (
                <div key={value.id} className="p-4 border rounded-lg space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <DynamicIcon iconName={value.icon || 'fa:Circle'} className="w-5 h-5 text-primary" />
                      <span className="text-xs text-slate-500">{value.icon || '-'}</span>
                    </div>
                    <div className="flex gap-2">
                      <Button variant="outline" size="sm" onClick={() => handleOpenValueDialog(value)}>
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button variant="destructive" size="sm" onClick={() => handleDeleteValue(value.id)}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                  <div className={`flex flex-col ${isRTL ? 'text-right' : 'text-left'}`} style={{ textAlign: isRTL ? 'right' : 'left', direction: isRTL ? 'rtl' : 'ltr' }}>
                    <strong className="text-xs font-medium text-slate-500 mb-2">{t('content.value.title') || 'العنوان'}:</strong>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                      <div className="p-2 bg-white dark:bg-slate-800 rounded border border-slate-200 dark:border-slate-700">
                        <span className="text-xs text-slate-400">🇬🇧 EN</span>
                        <p className="text-sm font-medium">
                          {typeof value.title === 'string'
                            ? value.title
                            : (typeof value.title === 'object' && value.title?.en 
                              ? value.title.en 
                              : (value.title?.ar || '-'))}
                        </p>
                      </div>
                      <div className="p-2 bg-white dark:bg-slate-800 rounded border border-slate-200 dark:border-slate-700">
                        <span className="text-xs text-slate-400">🇸🇦 AR</span>
                        <p className="text-sm font-medium">
                          {typeof value.title === 'string'
                            ? value.title
                            : (typeof value.title === 'object' && value.title?.ar 
                              ? value.title.ar 
                              : (value.title?.en || '-'))}
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className={`flex flex-col ${isRTL ? 'text-right' : 'text-left'}`} style={{ textAlign: isRTL ? 'right' : 'left', direction: isRTL ? 'rtl' : 'ltr' }}>
                    <strong className="text-xs font-medium text-slate-500 mb-2">{t('content.value.content') || 'المحتوى'}:</strong>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                      <div className="p-2 bg-white dark:bg-slate-800 rounded border border-slate-200 dark:border-slate-700">
                        <span className="text-xs text-slate-400">🇬🇧 EN</span>
                        <p className="text-sm text-slate-600 dark:text-slate-300">
                          {typeof value.content === 'string'
                            ? value.content
                            : (typeof value.content === 'object' && value.content?.en 
                              ? value.content.en 
                              : (value.content?.ar || '-'))}
                        </p>
                      </div>
                      <div className="p-2 bg-white dark:bg-slate-800 rounded border border-slate-200 dark:border-slate-700">
                        <span className="text-xs text-slate-400">🇸🇦 AR</span>
                        <p className="text-sm text-slate-600 dark:text-slate-300">
                          {typeof value.content === 'string'
                            ? value.content
                            : (typeof value.content === 'object' && value.content?.ar 
                              ? value.content.ar 
                              : (value.content?.en || '-'))}
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-4 text-xs text-slate-500">
                    <span>{t('content.value.order') || 'الترتيب'}: {value.order}</span>
                    <span>{t('content.value.status') || 'الحالة'}: {value.is_active ? '✅' : '❌'}</span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-muted-foreground">
              {t('content.about.noValues') || 'لا توجد قيم. اضغط على إضافة قيمة لإضافة قيمة جديدة.'}
            </p>
          )}
        </CardContent>
      </Card>

      {/* About Content Dialog */}
      <AboutContentDialog
        isOpen={isContentDialogOpen}
        onClose={() => setIsContentDialogOpen(false)}
        content={aboutContent?.data}
        onSubmit={createOrUpdateContent.mutate}
      />

      {/* Value Dialog */}
      <ValueDialog
        isOpen={isValueDialogOpen}
        onClose={() => {
          setIsValueDialogOpen(false);
          setEditingValue(null);
        }}
        value={editingValue}
        onCreate={createValue.mutate}
        onUpdate={updateValue.mutate}
      />
    </div>
  );
};

// How It Works Content Section Component
const HowItWorksContentSection = () => {
  const { t, isRTL } = useLanguage();
  const [isContentDialogOpen, setIsContentDialogOpen] = useState(false);
  const [isStepDialogOpen, setIsStepDialogOpen] = useState(false);
  const [editingStep, setEditingStep] = useState<HowItWorksStep | null>(null);

  const { data: howItWorksContent, isLoading: contentLoading } = useHowItWorksContent();
  const { data: stepsData, isLoading: stepsLoading } = useHowItWorksSteps();

  const createOrUpdateContent = useCreateOrUpdateHowItWorksContent();
  const createStep = useCreateHowItWorksStep();
  const updateStep = useUpdateHowItWorksStep();
  const deleteStep = useDeleteHowItWorksStep();

  const steps = stepsData?.data || [];

  const handleOpenStepDialog = (step?: HowItWorksStep) => {
    setEditingStep(step || null);
    setIsStepDialogOpen(true);
  };

  const handleDeleteStep = (id: number) => {
    if (window.confirm(t('content.step.confirmDelete') || 'هل أنت متأكد من حذف هذه الخطوة؟')) {
      deleteStep.mutate(id);
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>{t('content.howItWorks.title') || 'محتوى صفحة كيف يعمل'}</CardTitle>
              <CardDescription>
                {t('content.howItWorks.description') || 'إدارة محتوى صفحة كيف يعمل'}
              </CardDescription>
            </div>
            <Button onClick={() => setIsContentDialogOpen(true)}>
              <Plus className={`w-4 h-4 ${isRTL ? 'mr-2' : 'ml-2'}`} />
              {t('content.howItWorks.edit') || 'تعديل المحتوى'}
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {contentLoading ? (
            <Skeleton className="h-32 w-full" />
          ) : howItWorksContent?.data ? (
            <div className="space-y-4">
              {/* Hero Title */}
              <div className={`flex flex-col ${isRTL ? 'text-right' : 'text-left'}`} style={{ textAlign: isRTL ? 'right' : 'left', direction: isRTL ? 'rtl' : 'ltr' }}>
                <strong className="text-sm font-semibold mb-3">{t('content.howItWorks.heroTitle') || 'العنوان الرئيسي'}:</strong>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div className="p-3 bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700">
                    <span className="text-xs font-medium text-slate-500 dark:text-slate-400">🇬🇧 English</span>
                    <p className="mt-1 text-sm font-medium text-slate-900 dark:text-slate-100">
                      {typeof howItWorksContent.data.hero_title === 'string'
                        ? howItWorksContent.data.hero_title
                        : (typeof howItWorksContent.data.hero_title === 'object' && howItWorksContent.data.hero_title?.en 
                          ? howItWorksContent.data.hero_title.en 
                          : (howItWorksContent.data.hero_title?.ar || '-'))}
                    </p>
                  </div>
                  <div className="p-3 bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700">
                    <span className="text-xs font-medium text-slate-500 dark:text-slate-400">🇸🇦 Arabic</span>
                    <p className="mt-1 text-sm font-medium text-slate-900 dark:text-slate-100">
                      {typeof howItWorksContent.data.hero_title === 'string'
                        ? howItWorksContent.data.hero_title
                        : (typeof howItWorksContent.data.hero_title === 'object' && howItWorksContent.data.hero_title?.ar 
                          ? howItWorksContent.data.hero_title.ar 
                          : (howItWorksContent.data.hero_title?.en || '-'))}
                    </p>
                  </div>
                </div>
              </div>
              {/* Hero Subtitle */}
              <div className={`flex flex-col ${isRTL ? 'text-right' : 'text-left'}`} style={{ textAlign: isRTL ? 'right' : 'left', direction: isRTL ? 'rtl' : 'ltr' }}>
                <strong className="text-sm font-semibold mb-3">{t('content.howItWorks.heroSubtitle') || 'الوصف الرئيسي'}:</strong>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div className="p-3 bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700">
                    <span className="text-xs font-medium text-slate-500 dark:text-slate-400">🇬🇧 English</span>
                    <p className="mt-1 text-sm text-slate-700 dark:text-slate-300">
                      {typeof howItWorksContent.data.hero_subtitle === 'string'
                        ? howItWorksContent.data.hero_subtitle
                        : (typeof howItWorksContent.data.hero_subtitle === 'object' && howItWorksContent.data.hero_subtitle?.en 
                          ? howItWorksContent.data.hero_subtitle.en 
                          : (howItWorksContent.data.hero_subtitle?.ar || '-'))}
                    </p>
                  </div>
                  <div className="p-3 bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700">
                    <span className="text-xs font-medium text-slate-500 dark:text-slate-400">🇸🇦 Arabic</span>
                    <p className="mt-1 text-sm text-slate-700 dark:text-slate-300">
                      {typeof howItWorksContent.data.hero_subtitle === 'string'
                        ? howItWorksContent.data.hero_subtitle
                        : (typeof howItWorksContent.data.hero_subtitle === 'object' && howItWorksContent.data.hero_subtitle?.ar 
                          ? howItWorksContent.data.hero_subtitle.ar 
                          : (howItWorksContent.data.hero_subtitle?.en || '-'))}
                    </p>
                  </div>
                </div>
              </div>
              {/* CTA Button Text */}
              <div className={`flex flex-col ${isRTL ? 'text-right' : 'text-left'}`} style={{ textAlign: isRTL ? 'right' : 'left', direction: isRTL ? 'rtl' : 'ltr' }}>
                <strong className="text-sm font-semibold mb-3">{t('content.howItWorks.ctaButtonText') || 'نص زر الدعوة'}:</strong>
                <div className="grid grid-cols-2 gap-3">
                  <div className="p-3 bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700">
                    <span className="text-xs font-medium text-slate-500 dark:text-slate-400">🇬🇧 English</span>
                    <p className="mt-1 text-sm text-slate-700 dark:text-slate-300">
                      {typeof howItWorksContent.data.cta_button_text === 'string'
                        ? howItWorksContent.data.cta_button_text
                        : (typeof howItWorksContent.data.cta_button_text === 'object' && howItWorksContent.data.cta_button_text?.en 
                          ? howItWorksContent.data.cta_button_text.en 
                          : (howItWorksContent.data.cta_button_text?.ar || '-'))}
                    </p>
                  </div>
                  <div className="p-3 bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700">
                    <span className="text-xs font-medium text-slate-500 dark:text-slate-400">🇸🇦 Arabic</span>
                    <p className="mt-1 text-sm text-slate-700 dark:text-slate-300">
                      {typeof howItWorksContent.data.cta_button_text === 'string'
                        ? howItWorksContent.data.cta_button_text
                        : (typeof howItWorksContent.data.cta_button_text === 'object' && howItWorksContent.data.cta_button_text?.ar 
                          ? howItWorksContent.data.cta_button_text.ar 
                          : (howItWorksContent.data.cta_button_text?.en || '-'))}
                    </p>
                  </div>
                </div>
              </div>
              {/* CTA Button URL */}
              <div className={`flex flex-col ${isRTL ? 'text-right' : 'text-left'}`} style={{ textAlign: isRTL ? 'right' : 'left', direction: isRTL ? 'rtl' : 'ltr' }}>
                <strong className="text-sm font-semibold mb-3">{t('content.howItWorks.ctaButtonUrl') || 'رابط زر الدعوة'}:</strong>
                <div className="p-3 bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700">
                  <a href={howItWorksContent.data.cta_button_url || '#'} target="_blank" rel="noopener noreferrer" className="text-sm text-blue-600 dark:text-blue-400 hover:underline break-all">
                    {howItWorksContent.data.cta_button_url || '-'}
                  </a>
                </div>
              </div>
            </div>
          ) : (
            <p className="text-muted-foreground">
              {t('content.howItWorks.noContent') || 'لا يوجد محتوى. اضغط على تعديل المحتوى لإضافة محتوى جديد.'}
            </p>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>{t('content.howItWorks.steps') || 'الخطوات'}</CardTitle>
              <CardDescription>
                {t('content.howItWorks.stepsDescription') || 'إدارة خطوات كيف يعمل'}
              </CardDescription>
            </div>
            <Button onClick={() => handleOpenStepDialog()}>
              <Plus className={`w-4 h-4 ${isRTL ? 'mr-2' : 'ml-2'}`} />
              {t('content.howItWorks.addStep') || 'إضافة خطوة'}
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {stepsLoading ? (
            <div className="space-y-4">
              <Skeleton className="h-24 w-full" />
              <Skeleton className="h-24 w-full" />
            </div>
          ) : steps.length > 0 ? (
            <div className="space-y-4">
              {steps.map((step) => (
                <div key={step.id} className="p-4 border rounded-lg space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <DynamicIcon iconName={step.icon || 'fa:Circle'} className="w-5 h-5 text-primary" />
                      <span className="text-xs text-slate-500">{step.icon || '-'}</span>
                    </div>
                    <div className="flex gap-2">
                      <Button variant="outline" size="sm" onClick={() => handleOpenStepDialog(step)}>
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button variant="destructive" size="sm" onClick={() => handleDeleteStep(step.id)}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                  <div className={`flex flex-col ${isRTL ? 'text-right' : 'text-left'}`} style={{ textAlign: isRTL ? 'right' : 'left', direction: isRTL ? 'rtl' : 'ltr' }}>
                    <strong className="text-xs font-medium text-slate-500 mb-2">{t('content.step.title') || 'العنوان'}:</strong>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                      <div className="p-2 bg-white dark:bg-slate-800 rounded border border-slate-200 dark:border-slate-700">
                        <span className="text-xs text-slate-400">🇬🇧 EN</span>
                        <p className="text-sm font-medium">
                          {typeof step.title === 'string'
                            ? step.title
                            : (typeof step.title === 'object' && step.title?.en 
                              ? step.title.en 
                              : (step.title?.ar || '-'))}
                        </p>
                      </div>
                      <div className="p-2 bg-white dark:bg-slate-800 rounded border border-slate-200 dark:border-slate-700">
                        <span className="text-xs text-slate-400">🇸🇦 AR</span>
                        <p className="text-sm font-medium">
                          {typeof step.title === 'string'
                            ? step.title
                            : (typeof step.title === 'object' && step.title?.ar 
                              ? step.title.ar 
                              : (step.title?.en || '-'))}
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className={`flex flex-col ${isRTL ? 'text-right' : 'text-left'}`} style={{ textAlign: isRTL ? 'right' : 'left', direction: isRTL ? 'rtl' : 'ltr' }}>
                    <strong className="text-xs font-medium text-slate-500 mb-2">{t('content.step.description') || 'الوصف'}:</strong>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                      <div className="p-2 bg-white dark:bg-slate-800 rounded border border-slate-200 dark:border-slate-700">
                        <span className="text-xs text-slate-400">🇬🇧 EN</span>
                        <p className="text-sm text-slate-600 dark:text-slate-300">
                          {typeof step.description === 'string'
                            ? step.description
                            : (typeof step.description === 'object' && step.description?.en 
                              ? step.description.en 
                              : (step.description?.ar || '-'))}
                        </p>
                      </div>
                      <div className="p-2 bg-white dark:bg-slate-800 rounded border border-slate-200 dark:border-slate-700">
                        <span className="text-xs text-slate-400">🇸🇦 AR</span>
                        <p className="text-sm text-slate-600 dark:text-slate-300">
                          {typeof step.description === 'string'
                            ? step.description
                            : (typeof step.description === 'object' && step.description?.ar 
                              ? step.description.ar 
                              : (step.description?.en || '-'))}
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-4 text-xs text-slate-500">
                    <span>{t('content.step.stepNumber') || 'رقم الخطوة'}: {step.step_number}</span>
                    <span>{t('content.step.status') || 'الحالة'}: {step.is_active ? '✅' : '❌'}</span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-muted-foreground">
              {t('content.howItWorks.noSteps') || 'لا توجد خطوات. اضغط على إضافة خطوة لإضافة خطوة جديدة.'}
            </p>
          )}
        </CardContent>
      </Card>

      {/* How It Works Content Dialog */}
      <HowItWorksContentDialog
        isOpen={isContentDialogOpen}
        onClose={() => setIsContentDialogOpen(false)}
        content={howItWorksContent?.data}
        onSubmit={createOrUpdateContent.mutate}
      />

      {/* Step Dialog */}
      <StepDialog
        isOpen={isStepDialogOpen}
        onClose={() => {
          setIsStepDialogOpen(false);
          setEditingStep(null);
        }}
        step={editingStep}
        onCreate={createStep.mutate}
        onUpdate={updateStep.mutate}
      />
    </div>
  );
};

// FAQs Content Section Component
const FAQsContentSection = () => {
  const { t, isRTL } = useLanguage();
  const [isFAQDialogOpen, setIsFAQDialogOpen] = useState(false);
  const [editingFAQ, setEditingFAQ] = useState<FAQ | null>(null);

  const { data: faqsData, isLoading: faqsLoading } = useFAQs();

  const createFAQ = useCreateFAQ();
  const updateFAQ = useUpdateFAQ();
  const deleteFAQ = useDeleteFAQ();

  const faqs = faqsData?.data || [];

  const handleOpenFAQDialog = (faq?: FAQ) => {
    setEditingFAQ(faq || null);
    setIsFAQDialogOpen(true);
  };

  const handleDeleteFAQ = (id: number) => {
    if (window.confirm(t('content.faq.confirmDelete') || 'هل أنت متأكد من حذف هذا السؤال؟')) {
      deleteFAQ.mutate(id);
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle>{t('content.faqs.title') || 'الأسئلة الشائعة'}</CardTitle>
            <CardDescription>
              {t('content.faqs.description') || 'إدارة الأسئلة الشائعة'}
            </CardDescription>
          </div>
          <Button onClick={() => handleOpenFAQDialog()}>
            <Plus className={`w-4 h-4 ${isRTL ? 'mr-2' : 'ml-2'}`} />
            {t('content.faqs.add') || 'إضافة سؤال'}
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {faqsLoading ? (
          <div className="space-y-4">
            <Skeleton className="h-24 w-full" />
            <Skeleton className="h-24 w-full" />
          </div>
        ) : faqs.length > 0 ? (
          <div className="space-y-4">
            {faqs.map((faq) => (
              <div key={faq.id} className="p-4 border rounded-lg space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-xs text-slate-500">{t('content.faq.order') || 'الترتيب'}: {faq.order}</span>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={() => handleOpenFAQDialog(faq)}>
                      <Edit className="w-4 h-4" />
                    </Button>
                    <Button variant="destructive" size="sm" onClick={() => handleDeleteFAQ(faq.id)}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
                <div className={`flex flex-col ${isRTL ? 'text-right' : 'text-left'}`} style={{ textAlign: isRTL ? 'right' : 'left', direction: isRTL ? 'rtl' : 'ltr' }}>
                  <strong className="text-xs font-medium text-slate-500 mb-2">{t('content.faq.question') || 'السؤال'}:</strong>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    <div className="p-2 bg-white dark:bg-slate-800 rounded border border-slate-200 dark:border-slate-700">
                      <span className="text-xs text-slate-400">🇬🇧 EN</span>
                      <p className="text-sm font-medium">
                        {typeof faq.question === 'string'
                          ? faq.question
                          : (typeof faq.question === 'object' && faq.question?.en 
                            ? faq.question.en 
                            : (faq.question?.ar || '-'))}
                      </p>
                    </div>
                    <div className="p-2 bg-white dark:bg-slate-800 rounded border border-slate-200 dark:border-slate-700">
                      <span className="text-xs text-slate-400">🇸🇦 AR</span>
                      <p className="text-sm font-medium">
                        {typeof faq.question === 'string'
                          ? faq.question
                          : (typeof faq.question === 'object' && faq.question?.ar 
                            ? faq.question.ar 
                            : (faq.question?.en || '-'))}
                      </p>
                    </div>
                  </div>
                </div>
                <div className={`flex flex-col ${isRTL ? 'text-right' : 'text-left'}`} style={{ textAlign: isRTL ? 'right' : 'left', direction: isRTL ? 'rtl' : 'ltr' }}>
                  <strong className="text-xs font-medium text-slate-500 mb-2">{t('content.faq.answer') || 'الجواب'}:</strong>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    <div className="p-2 bg-white dark:bg-slate-800 rounded border border-slate-200 dark:border-slate-700">
                      <span className="text-xs text-slate-400">🇬🇧 EN</span>
                      <p className="text-sm text-slate-600 dark:text-slate-300">
                        {typeof faq.answer === 'string'
                          ? faq.answer
                          : (typeof faq.answer === 'object' && faq.answer?.en 
                            ? faq.answer.en 
                            : (faq.answer?.ar || '-'))}
                      </p>
                    </div>
                    <div className="p-2 bg-white dark:bg-slate-800 rounded border border-slate-200 dark:border-slate-700">
                      <span className="text-xs text-slate-400">🇸🇦 AR</span>
                      <p className="text-sm text-slate-600 dark:text-slate-300">
                        {typeof faq.answer === 'string'
                          ? faq.answer
                          : (typeof faq.answer === 'object' && faq.answer?.ar 
                            ? faq.answer.ar 
                            : (faq.answer?.en || '-'))}
                      </p>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-4 text-xs text-slate-500">
                  <span>{t('content.faq.status') || 'الحالة'}: {faq.is_active ? '✅' : '❌'}</span>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-muted-foreground">
            {t('content.faqs.noFaqs') || 'لا توجد أسئلة. اضغط على إضافة سؤال لإضافة سؤال جديد.'}
          </p>
        )}
      </CardContent>

      {/* FAQ Dialog */}
      <FAQDialog
        isOpen={isFAQDialogOpen}
        onClose={() => {
          setIsFAQDialogOpen(false);
          setEditingFAQ(null);
        }}
        faq={editingFAQ}
        onCreate={createFAQ.mutate}
        onUpdate={updateFAQ.mutate}
      />
    </Card>
  );
};

// Contact Content Section Component
const ContactContentSection = () => {
  const { t, isRTL } = useLanguage();
  const [isContentDialogOpen, setIsContentDialogOpen] = useState(false);

  const { data: contactContent, isLoading: contentLoading } = useContactContent();
  const createOrUpdateContent = useCreateOrUpdateContactContent();

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle>{t('content.contact.title') || 'محتوى صفحة التواصل'}</CardTitle>
            <CardDescription>
              {t('content.contact.description') || 'إدارة محتوى صفحة التواصل'}
            </CardDescription>
          </div>
          <Button onClick={() => setIsContentDialogOpen(true)}>
            <Plus className={`w-4 h-4 ${isRTL ? 'mr-2' : 'ml-2'}`} />
            {t('content.contact.edit') || 'تعديل المحتوى'}
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {contentLoading ? (
          <Skeleton className="h-32 w-full" />
        ) : contactContent?.data ? (
          <div className="space-y-4">
            <div className={`flex flex-col ${isRTL ? 'text-right' : 'text-left'}`} style={{ textAlign: isRTL ? 'right' : 'left', direction: isRTL ? 'rtl' : 'ltr' }}>
              <strong className="text-sm font-semibold mb-3">{t('content.contact.heroTitle') || 'العنوان الرئيسي'}:</strong>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="p-3 bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700">
                  <span className="text-xs font-medium text-slate-500 dark:text-slate-400">🇸🇦 English</span>
                  <p className="mt-1 text-sm font-medium text-slate-900 dark:text-slate-100">
                    {typeof contactContent.data.hero_title === 'string'
                      ? contactContent.data.hero_title
                      : (typeof contactContent.data.hero_title === 'object' && contactContent.data.hero_title?.en 
                        ? contactContent.data.hero_title.en 
                        : (contactContent.data.hero_title?.ar || '-'))}
                  </p>
                </div>
                <div className="p-3 bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700">
                  <span className="text-xs font-medium text-slate-500 dark:text-slate-400">🇸🇦 Arabic</span>
                  <p className="mt-1 text-sm font-medium text-slate-900 dark:text-slate-100">
                    {typeof contactContent.data.hero_title === 'string'
                      ? contactContent.data.hero_title
                      : (typeof contactContent.data.hero_title === 'object' && contactContent.data.hero_title?.ar 
                        ? contactContent.data.hero_title.ar 
                        : (contactContent.data.hero_title?.en || '-'))}
                  </p>
                </div>
              </div>
            </div>
            <div className={`flex flex-col ${isRTL ? 'text-right' : 'text-left'}`} style={{ textAlign: isRTL ? 'right' : 'left', direction: isRTL ? 'rtl' : 'ltr' }}>
              <strong className="text-sm font-semibold mb-3">{t('content.contact.heroSubtitleEn') || 'الوصف الرئيسي'}:</strong>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="p-3 bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700">
                  <span className="text-xs font-medium text-slate-500 dark:text-slate-400">🇸🇦 English</span>
                  <p className="mt-1 text-sm text-slate-700 dark:text-slate-300">
                    {typeof contactContent.data.hero_subtitle === 'string'
                      ? contactContent.data.hero_subtitle
                      : (typeof contactContent.data.hero_subtitle === 'object' && contactContent.data.hero_subtitle?.en 
                        ? contactContent.data.hero_subtitle.en 
                        : (contactContent.data.hero_subtitle?.ar || '-'))}
                  </p>
                </div>
                <div className="p-3 bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700">
                  <span className="text-xs font-medium text-slate-500 dark:text-slate-400">🇸🇦 Arabic</span>
                  <p className="mt-1 text-sm text-slate-700 dark:text-slate-300">
                    {typeof contactContent.data.hero_subtitle === 'string'
                      ? contactContent.data.hero_subtitle
                      : (typeof contactContent.data.hero_subtitle === 'object' && contactContent.data.hero_subtitle?.ar 
                        ? contactContent.data.hero_subtitle.ar 
                        : (contactContent.data.hero_subtitle?.en || '-'))}
                  </p>
                </div>
              </div>
            </div>
            <div className={`flex flex-col ${isRTL ? 'text-right' : 'text-left'}`} style={{ textAlign: isRTL ? 'right' : 'left', direction: isRTL ? 'rtl' : 'ltr' }}>
              <strong className="text-sm font-semibold mb-3">{t('content.contact.formTitleEn') || 'عنوان النموذج'}:</strong>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="p-3 bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700">
                  <span className="text-xs font-medium text-slate-500 dark:text-slate-400">🇸🇦 English</span>
                  <p className="mt-1 text-sm text-slate-700 dark:text-slate-300">
                    {typeof contactContent.data.form_title === 'string'
                      ? contactContent.data.form_title
                      : (typeof contactContent.data.form_title === 'object' && contactContent.data.form_title?.en 
                        ? contactContent.data.form_title.en 
                        : (contactContent.data.form_title?.ar || '-'))}
                  </p>
                </div>
                <div className="p-3 bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700">
                  <span className="text-xs font-medium text-slate-500 dark:text-slate-400">🇸🇦 Arabic</span>
                  <p className="mt-1 text-sm text-slate-700 dark:text-slate-300">
                    {typeof contactContent.data.form_title === 'string'
                      ? contactContent.data.form_title
                      : (typeof contactContent.data.form_title === 'object' && contactContent.data.form_title?.ar 
                        ? contactContent.data.form_title.ar 
                        : (contactContent.data.form_title?.en || '-'))}
                  </p>
                </div>
              </div>
            </div>
            <div className={`flex flex-col ${isRTL ? 'text-right' : 'text-left'}`} style={{ textAlign: isRTL ? 'right' : 'left', direction: isRTL ? 'rtl' : 'ltr' }}>
              <strong className="text-sm font-semibold mb-3">{t('content.contact.phone') || 'الهاتف'}:</strong>
              <div className="p-3 bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700">
                <p className="text-sm text-slate-700 dark:text-slate-300">{contactContent.data.phone || '-'}</p>
              </div>
            </div>
            <div className={`flex flex-col ${isRTL ? 'text-right' : 'text-left'}`} style={{ textAlign: isRTL ? 'right' : 'left', direction: isRTL ? 'rtl' : 'ltr' }}>
              <strong className="text-sm font-semibold mb-3">{t('content.contact.email') || 'البريد الإلكتروني'}:</strong>
              <div className="p-3 bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700">
                <p className="text-sm text-slate-700 dark:text-slate-300">{contactContent.data.email || '-'}</p>
              </div>
            </div>
            <div className={`flex flex-col ${isRTL ? 'text-right' : 'text-left'}`} style={{ textAlign: isRTL ? 'right' : 'left', direction: isRTL ? 'rtl' : 'ltr' }}>
              <strong className="text-sm font-semibold mb-3">{t('content.contact.addressEn') || 'العنوان'}:</strong>
              <div className="p-3 bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700">
                <p className="text-sm text-slate-700 dark:text-slate-300">
                  {(() => {
                    const address = contactContent.data.address;
                    if (typeof address === 'string') return address;
                    if (typeof address === 'object' && address !== null) {
                      return address.en || address.ar || '-';
                    }
                    return '-';
                  })()}
                </p>
              </div>
            </div>
            <div className={`flex flex-col ${isRTL ? 'text-right' : 'text-left'}`} style={{ textAlign: isRTL ? 'right' : 'left', direction: isRTL ? 'rtl' : 'ltr' }}>
              <strong className="text-sm font-semibold mb-3">{t('content.contact.phoneIcon') || 'أيقونة الهاتف'}:</strong>
              <div className="p-3 bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700">
                <p className="text-sm text-slate-700 dark:text-slate-300">{contactContent.data.phone_icon || '-'}</p>
              </div>
            </div>
            <div className={`flex flex-col ${isRTL ? 'text-right' : 'text-left'}`} style={{ textAlign: isRTL ? 'right' : 'left', direction: isRTL ? 'rtl' : 'ltr' }}>
              <strong className="text-sm font-semibold mb-3">{t('content.contact.emailIcon') || 'أيقونة البريد'}:</strong>
              <div className="p-3 bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700">
                <p className="text-sm text-slate-700 dark:text-slate-300">{contactContent.data.email_icon || '-'}</p>
              </div>
            </div>
            <div className={`flex flex-col ${isRTL ? 'text-right' : 'text-left'}`} style={{ textAlign: isRTL ? 'right' : 'left', direction: isRTL ? 'rtl' : 'ltr' }}>
              <strong className="text-sm font-semibold mb-3">{t('content.contact.addressIcon') || 'أيقونة العنوان'}:</strong>
              <div className="p-3 bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700">
                <p className="text-sm text-slate-700 dark:text-slate-300">{contactContent.data.address_icon || '-'}</p>
              </div>
            </div>
          </div>
        ) : (
          <p className="text-muted-foreground">
            {t('content.contact.noContent') || 'لا يوجد محتوى. اضغط على تعديل المحتوى لإضافة محتوى جديد.'}
          </p>
        )}
      </CardContent>

      {/* Contact Content Dialog */}
      <ContactContentDialog
        isOpen={isContentDialogOpen}
        onClose={() => setIsContentDialogOpen(false)}
        content={contactContent?.data}
        onSubmit={createOrUpdateContent.mutate}
      />
    </Card>
  );
};

// Navigation Content Section Component
const NavigationContentSection = () => {
  const { t, isRTL } = useLanguage();
  const [isLinkDialogOpen, setIsLinkDialogOpen] = useState(false);
  const [editingLink, setEditingLink] = useState<NavigationLink | null>(null);

  const { data: linksData, isLoading: linksLoading } = useNavigationLinks();

  const createLink = useCreateNavigationLink();
  const updateLink = useUpdateNavigationLink();
  const deleteLink = useDeleteNavigationLink();

  const links = linksData?.data || [];

  const handleOpenLinkDialog = (link?: NavigationLink) => {
    setEditingLink(link || null);
    setIsLinkDialogOpen(true);
  };

  const handleDeleteLink = (id: number) => {
    if (window.confirm(t('content.navigation.confirmDelete') || 'هل أنت متأكد من حذف هذا الرابط؟')) {
      deleteLink.mutate(id);
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle>{t('content.navigation.title') || 'روابط التنقل'}</CardTitle>
            <CardDescription>
              {t('content.navigation.description') || 'إدارة روابط القائمة الرئيسية'}
            </CardDescription>
          </div>
          <Button onClick={() => handleOpenLinkDialog()}>
            <Plus className={`w-4 h-4 ${isRTL ? 'mr-2' : 'ml-2'}`} />
            {t('content.navigation.add') || 'إضافة رابط'}
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {linksLoading ? (
          <div className="space-y-4">
            <Skeleton className="h-24 w-full" />
            <Skeleton className="h-24 w-full" />
          </div>
        ) : links.length > 0 ? (
          <div className="space-y-4">
            {links.map((link) => (
              <div key={link.id} className="p-4 border rounded-lg space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-xs text-slate-500">{t('content.navigation.order') || 'الترتيب'}: {link.order}</span>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={() => handleOpenLinkDialog(link)}>
                      <Edit className="w-4 h-4" />
                    </Button>
                    <Button variant="destructive" size="sm" onClick={() => handleDeleteLink(link.id)}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
                <div className={`flex flex-col ${isRTL ? 'text-right' : 'text-left'}`} style={{ textAlign: isRTL ? 'right' : 'left', direction: isRTL ? 'rtl' : 'ltr' }}>
                  <strong className="text-xs font-medium text-slate-500 mb-2">{t('content.navigation.label') || 'التسمية'}:</strong>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    <div className="p-2 bg-white dark:bg-slate-800 rounded border border-slate-200 dark:border-slate-700">
                      <span className="text-xs text-slate-400">🇬🇧 EN</span>
                      <p className="text-sm font-medium">
                        {typeof link.label === 'string'
                          ? link.label
                          : (typeof link.label === 'object' && link.label?.en 
                            ? link.label.en 
                            : (link.label?.ar || '-'))}
                      </p>
                    </div>
                    <div className="p-2 bg-white dark:bg-slate-800 rounded border border-slate-200 dark:border-slate-700">
                      <span className="text-xs text-slate-400">🇸🇦 AR</span>
                      <p className="text-sm font-medium">
                        {typeof link.label === 'string'
                          ? link.label
                          : (typeof link.label === 'object' && link.label?.ar 
                            ? link.label.ar 
                            : (link.label?.en || '-'))}
                      </p>
                    </div>
                  </div>
                </div>
                <div className={`flex flex-col ${isRTL ? 'text-right' : 'text-left'}`} style={{ textAlign: isRTL ? 'right' : 'left', direction: isRTL ? 'rtl' : 'ltr' }}>
                  <strong className="text-xs font-medium text-slate-500 mb-2">{t('content.navigation.url') || 'الرابط'}:</strong>
                  <div className="p-2 bg-white dark:bg-slate-800 rounded border border-slate-200 dark:border-slate-700">
                    <a href={link.url} target="_blank" rel="noopener noreferrer" className="text-sm text-blue-600 dark:text-blue-400 hover:underline break-all">
                      {link.url || '-'}
                    </a>
                  </div>
                </div>
                <div className="flex items-center gap-4 text-xs text-slate-500">
                  <span>{t('content.navigation.isExternal') || 'خارجي'}: {link.is_external ? '✅' : '❌'}</span>
                  <span>{t('content.navigation.status') || 'الحالة'}: {link.is_active ? '✅' : '❌'}</span>
                  {link.route_name && <span>{t('content.navigation.routeName') || 'اسم المسار'}: {link.route_name}</span>}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-muted-foreground">
            {t('content.navigation.noLinks') || 'لا توجد روابط. اضغط على إضافة رابط لإضافة رابط جديد.'}
          </p>
        )}
      </CardContent>

      {/* Navigation Link Dialog */}
      <NavigationLinkDialog
        isOpen={isLinkDialogOpen}
        onClose={() => {
          setIsLinkDialogOpen(false);
          setEditingLink(null);
        }}
        link={editingLink}
        onCreate={createLink.mutate}
        onUpdate={updateLink.mutate}
      />
    </Card>
  );
};

// Footer Content Section Component
const FooterContentSection = () => {
  const { t, isRTL } = useLanguage();
  const [isContentDialogOpen, setIsContentDialogOpen] = useState(false);

  const { data: footerContent, isLoading: contentLoading } = useFooterContent();
  const createOrUpdateContent = useCreateOrUpdateFooterContent();

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle>{t('content.footer.title') || 'محتوى التذييل'}</CardTitle>
            <CardDescription>
              {t('content.footer.description') || 'إدارة محتوى التذييل'}
            </CardDescription>
          </div>
          <Button onClick={() => setIsContentDialogOpen(true)}>
            <Plus className={`w-4 h-4 ${isRTL ? 'mr-2' : 'ml-2'}`} />
            {t('content.footer.edit') || 'تعديل المحتوى'}
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {contentLoading ? (
          <Skeleton className="h-32 w-full" />
        ) : footerContent?.data ? (
          <div className="space-y-4">
            {/* Company Description */}
            <div className={`flex flex-col ${isRTL ? 'text-right' : 'text-left'}`} style={{ textAlign: isRTL ? 'right' : 'left', direction: isRTL ? 'rtl' : 'ltr' }}>
              <strong className="text-sm font-semibold mb-3">{t('content.footer.companyDescription') || 'وصف الشركة'}:</strong>
              <div className="p-3 bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700">
                <p className="text-sm text-slate-700 dark:text-slate-300">
                  {typeof footerContent.data.company_description === 'string'
                    ? footerContent.data.company_description
                    : (typeof footerContent.data.company_description === 'object' && footerContent.data.company_description?.en 
                      ? footerContent.data.company_description.en 
                      : (footerContent.data.company_description?.ar || '-'))}
                </p>
              </div>
            </div>
            {/* Quick Links Title */}
            <div className={`flex flex-col ${isRTL ? 'text-right' : 'text-left'}`} style={{ textAlign: isRTL ? 'right' : 'left', direction: isRTL ? 'rtl' : 'ltr' }}>
              <strong className="text-sm font-semibold mb-3">{t('content.footer.quickLinksTitle') || 'عنوان الروابط السريعة'}:</strong>
              <div className="grid grid-cols-2 gap-3">
                <div className="p-3 bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700">
                  <span className="text-xs font-medium text-slate-500 dark:text-slate-400">🇬🇧 English</span>
                  <p className="mt-1 text-sm text-slate-700 dark:text-slate-300">
                    {typeof footerContent.data.quick_links_title === 'string'
                      ? footerContent.data.quick_links_title
                      : (typeof footerContent.data.quick_links_title === 'object' && footerContent.data.quick_links_title?.en 
                        ? footerContent.data.quick_links_title.en 
                        : (footerContent.data.quick_links_title?.ar || '-'))}
                  </p>
                </div>
                <div className="p-3 bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700">
                  <span className="text-xs font-medium text-slate-500 dark:text-slate-400">🇸🇦 Arabic</span>
                  <p className="mt-1 text-sm text-slate-700 dark:text-slate-300">
                    {typeof footerContent.data.quick_links_title === 'string'
                      ? footerContent.data.quick_links_title
                      : (typeof footerContent.data.quick_links_title === 'object' && footerContent.data.quick_links_title?.ar 
                        ? footerContent.data.quick_links_title.ar 
                        : (footerContent.data.quick_links_title?.en || '-'))}
                  </p>
                </div>
              </div>
            </div>
            {/* Legal Title */}
            <div className={`flex flex-col ${isRTL ? 'text-right' : 'text-left'}`} style={{ textAlign: isRTL ? 'right' : 'left', direction: isRTL ? 'rtl' : 'ltr' }}>
              <strong className="text-sm font-semibold mb-3">{t('content.footer.legalTitle') || 'عنوان القانونية'}:</strong>
              <div className="grid grid-cols-2 gap-3">
                <div className="p-3 bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700">
                  <span className="text-xs font-medium text-slate-500 dark:text-slate-400">🇬🇧 English</span>
                  <p className="mt-1 text-sm text-slate-700 dark:text-slate-300">
                    {typeof footerContent.data.legal_title === 'string'
                      ? footerContent.data.legal_title
                      : (typeof footerContent.data.legal_title === 'object' && footerContent.data.legal_title?.en 
                        ? footerContent.data.legal_title.en 
                        : (footerContent.data.legal_title?.ar || '-'))}
                  </p>
                </div>
                <div className="p-3 bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700">
                  <span className="text-xs font-medium text-slate-500 dark:text-slate-400">🇸🇦 Arabic</span>
                  <p className="mt-1 text-sm text-slate-700 dark:text-slate-300">
                    {typeof footerContent.data.legal_title === 'string'
                      ? footerContent.data.legal_title
                      : (typeof footerContent.data.legal_title === 'object' && footerContent.data.legal_title?.ar 
                        ? footerContent.data.legal_title.ar 
                        : (footerContent.data.legal_title?.en || '-'))}
                  </p>
                </div>
              </div>
            </div>
            {/* Contact Title */}
            <div className={`flex flex-col ${isRTL ? 'text-right' : 'text-left'}`} style={{ textAlign: isRTL ? 'right' : 'left', direction: isRTL ? 'rtl' : 'ltr' }}>
              <strong className="text-sm font-semibold mb-3">{t('content.footer.contactTitle') || 'عنوان التواصل'}:</strong>
              <div className="grid grid-cols-2 gap-3">
                <div className="p-3 bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700">
                  <span className="text-xs font-medium text-slate-500 dark:text-slate-400">🇬🇧 English</span>
                  <p className="mt-1 text-sm text-slate-700 dark:text-slate-300">
                    {typeof footerContent.data.contact_title === 'string'
                      ? footerContent.data.contact_title
                      : (typeof footerContent.data.contact_title === 'object' && footerContent.data.contact_title?.en 
                        ? footerContent.data.contact_title.en 
                        : (footerContent.data.contact_title?.ar || '-'))}
                  </p>
                </div>
                <div className="p-3 bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700">
                  <span className="text-xs font-medium text-slate-500 dark:text-slate-400">🇸🇦 Arabic</span>
                  <p className="mt-1 text-sm text-slate-700 dark:text-slate-300">
                    {typeof footerContent.data.contact_title === 'string'
                      ? footerContent.data.contact_title
                      : (typeof footerContent.data.contact_title === 'object' && footerContent.data.contact_title?.ar 
                        ? footerContent.data.contact_title.ar 
                        : (footerContent.data.contact_title?.en || '-'))}
                  </p>
                </div>
              </div>
            </div>
            {/* Phone */}
            <div className={`flex flex-col ${isRTL ? 'text-right' : 'text-left'}`} style={{ textAlign: isRTL ? 'right' : 'left', direction: isRTL ? 'rtl' : 'ltr' }}>
              <strong className="text-sm font-semibold mb-3">{t('content.footer.phone') || 'الهاتف'}:</strong>
              <div className="p-3 bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700">
                <p className="text-sm text-slate-700 dark:text-slate-300">{footerContent.data.phone || '-'}</p>
              </div>
            </div>
            {/* Email */}
            <div className={`flex flex-col ${isRTL ? 'text-right' : 'text-left'}`} style={{ textAlign: isRTL ? 'right' : 'left', direction: isRTL ? 'rtl' : 'ltr' }}>
              <strong className="text-sm font-semibold mb-3">{t('content.footer.email') || 'البريد الإلكتروني'}:</strong>
              <div className="p-3 bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700">
                <a href={`mailto:${footerContent.data.email}`} className="text-sm text-blue-600 dark:text-blue-400 hover:underline">
                  {footerContent.data.email || '-'}
                </a>
              </div>
            </div>
            {/* Location */}
            <div className={`flex flex-col ${isRTL ? 'text-right' : 'text-left'}`} style={{ textAlign: isRTL ? 'right' : 'left', direction: isRTL ? 'rtl' : 'ltr' }}>
              <strong className="text-sm font-semibold mb-3">{t('content.footer.location') || 'الموقع'}:</strong>
              <div className="p-3 bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700">
                {(() => {
                  const locationValue = typeof footerContent.data.location === 'string'
                    ? footerContent.data.location
                    : (typeof footerContent.data.location === 'object' && footerContent.data.location?.en 
                      ? footerContent.data.location.en 
                      : (footerContent.data.location?.ar || '-'));
                  return locationValue !== '-' ? (
                    <a 
                      href={locationValue} 
                      target="_blank" 
                      rel="noopener noreferrer" 
                      className="text-sm text-blue-600 dark:text-blue-400 hover:underline break-all"
                    >
                      {locationValue}
                    </a>
                  ) : (
                    <span className="text-sm text-slate-500">-</span>
                  );
                })()}
              </div>
            </div>
            {/* Copyright Text */}
            <div className={`flex flex-col ${isRTL ? 'text-right' : 'text-left'}`} style={{ textAlign: isRTL ? 'right' : 'left', direction: isRTL ? 'rtl' : 'ltr' }}>
              <strong className="text-sm font-semibold mb-3">{t('content.footer.copyrightText') || 'نص حقوق النشر'}:</strong>
              <div className="p-3 bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700">
                <p className="text-sm text-slate-700 dark:text-slate-300">{footerContent.data.copyright_text || '-'}</p>
              </div>
            </div>
            {/* Status */}
            <div className={`flex flex-col ${isRTL ? 'text-right' : 'text-left'}`} style={{ textAlign: isRTL ? 'right' : 'left', direction: isRTL ? 'rtl' : 'ltr' }}>
              <strong className="text-sm font-semibold mb-3">{t('content.footer.status') || 'الحالة'}:</strong>
              <div className="p-3 bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700">
                <span className={`text-sm font-medium ${footerContent.data.is_active ? 'text-green-600' : 'text-red-600'}`}>
                  {footerContent.data.is_active ? '✅ ' + (t('common.active') || 'نشط') : '❌ ' + (t('common.inactive') || 'غير نشط')}
                </span>
              </div>
            </div>
            {/* Created At */}
            <div className={`flex flex-col ${isRTL ? 'text-right' : 'text-left'}`} style={{ textAlign: isRTL ? 'right' : 'left', direction: isRTL ? 'rtl' : 'ltr' }}>
              <strong className="text-sm font-semibold mb-3">{t('content.footer.createdAt') || 'تاريخ الإنشاء'}:</strong>
              <div className="p-3 bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700">
                <p className="text-sm text-slate-700 dark:text-slate-300">
                  {new Date(footerContent.data.created_at).toLocaleString(isRTL ? 'ar-SA' : 'en-US')}
                </p>
              </div>
            </div>
            {/* Updated At */}
            <div className={`flex flex-col ${isRTL ? 'text-right' : 'text-left'}`} style={{ textAlign: isRTL ? 'right' : 'left', direction: isRTL ? 'rtl' : 'ltr' }}>
              <strong className="text-sm font-semibold mb-3">{t('content.footer.updatedAt') || 'تاريخ التحديث'}:</strong>
              <div className="p-3 bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700">
                <p className="text-sm text-slate-700 dark:text-slate-300">
                  {new Date(footerContent.data.updated_at).toLocaleString(isRTL ? 'ar-SA' : 'en-US')}
                </p>
              </div>
            </div>
          </div>
        ) : (
          <p className="text-muted-foreground">
            {t('content.footer.noContent') || 'لا يوجد محتوى. اضغط على تعديل المحتوى لإضافة محتوى جديد.'}
          </p>
        )}
      </CardContent>

      {/* Footer Content Dialog */}
      <FooterContentDialog
        isOpen={isContentDialogOpen}
        onClose={() => setIsContentDialogOpen(false)}
        content={footerContent?.data}
        onSubmit={createOrUpdateContent.mutate}
      />
    </Card>
  );
};

// Homepage Content Dialog Component
const HomepageContentDialog = ({ 
  isOpen, 
  onClose, 
  content,
  onSubmit 
}: { 
  isOpen: boolean; 
  onClose: () => void;
  content?: any;
  onSubmit: (data: any) => void;
}) => {
  const { t, isRTL } = useLanguage();
  const [formData, setFormData] = useState({
    hero_title: { en: '', ar: '' },
    hero_title_color: '#000000',
    hero_description: { en: '', ar: '' },
    hero_cta_text: { en: '', ar: '' },
    hero_cta_url: '',
    section_subtitle: { en: '', ar: '' },
    is_active: true,
  });

  useEffect(() => {
    if (content) {
      // Handle both string and TranslatedField formats from API
      const hero_title = typeof content.hero_title === 'string'
        ? { en: content.hero_title, ar: content.hero_title }
        : (content.hero_title || { en: '', ar: '' });
      const hero_description = typeof content.hero_description === 'string'
        ? { en: content.hero_description, ar: content.hero_description }
        : (content.hero_description || { en: '', ar: '' });
      const hero_cta_text = typeof content.hero_cta_text === 'string'
        ? { en: content.hero_cta_text, ar: content.hero_cta_text }
        : (content.hero_cta_text || { en: '', ar: '' });
      const section_subtitle = typeof content.section_subtitle === 'string'
        ? { en: content.section_subtitle, ar: content.section_subtitle }
        : (content.section_subtitle || { en: '', ar: '' });
      
      setFormData({
        hero_title,
        hero_title_color: content.hero_title_color || '#000000',
        hero_description,
        hero_cta_text,
        hero_cta_url: content.hero_cta_url || '',
        section_subtitle,
        is_active: content.is_active ?? true,
      });
    } else {
      setFormData({
        hero_title: { en: '', ar: '' },
        hero_title_color: '#000000',
        hero_description: { en: '', ar: '' },
        hero_cta_text: { en: '', ar: '' },
        hero_cta_url: '',
        section_subtitle: { en: '', ar: '' },
        is_active: true,
      });
    }
  }, [content]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(formData);
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t('content.homepage.edit') || 'تعديل محتوى الصفحة الرئيسية'}</DialogTitle>
          <DialogDescription>
            {t('content.homepage.editDescription') || 'قم بتعديل محتوى الصفحة الرئيسية'}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>{t('content.homepage.heroTitleEn') || 'عنوان الخلفية (إنجليزي)'}</Label>
              <Input
                value={formData.hero_title.en}
                onChange={(e) => setFormData({ ...formData, hero_title: { ...formData.hero_title, en: e.target.value } })}
                required
              />
            </div>
            <div>
              <Label>{t('content.homepage.heroTitleAr') || 'عنوان الخلفية (عربي)'}</Label>
              <Input
                value={formData.hero_title.ar}
                onChange={(e) => setFormData({ ...formData, hero_title: { ...formData.hero_title, ar: e.target.value } })}
                required
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>{t('content.homepage.heroTitleColor') || 'لون عنوان الخلفية'}</Label>
              <div className="flex items-center gap-2">
                <Input
                  type="color"
                  value={formData.hero_title_color}
                  onChange={(e) => setFormData({ ...formData, hero_title_color: e.target.value })}
                  className="w-20 h-10 border border-slate-200 rounded cursor-pointer"
                />
                <Input
                  value={formData.hero_title_color}
                  onChange={(e) => setFormData({ ...formData, hero_title_color: e.target.value })}
                  placeholder="#000000"
                  className="flex-1 bg-white border-slate-200 text-slate-900 placeholder-slate-400 focus:border-primary focus:ring-primary transition-all duration-300"
                />
              </div>
            </div>
            <div>
              <Label>{t('content.homepage.heroDescriptionEn') || 'وصف الخلفية (إنجليزي)'}</Label>
              <Textarea
                value={formData.hero_description.en}
                onChange={(e) => setFormData({ ...formData, hero_description: { ...formData.hero_description, en: e.target.value } })}
                rows={3}
                required
              />
            </div>
            <div>
              <Label>{t('content.homepage.heroDescriptionAr') || 'وصف الخلفية (عربي)'}</Label>
              <Textarea
                value={formData.hero_description.ar}
                onChange={(e) => setFormData({ ...formData, hero_description: { ...formData.hero_description, ar: e.target.value } })}
                rows={3}
                required
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>{t('content.homepage.heroCtaTextEn') || 'نص زر الدعوة (إنجليزي)'}</Label>
              <Input
                value={formData.hero_cta_text.en}
                onChange={(e) => setFormData({ ...formData, hero_cta_text: { ...formData.hero_cta_text, en: e.target.value } })}
                required
              />
            </div>
            <div>
              <Label>{t('content.homepage.heroCtaTextAr') || 'نص زر الدعوة (عربي)'}</Label>
              <Input
                value={formData.hero_cta_text.ar}
                onChange={(e) => setFormData({ ...formData, hero_cta_text: { ...formData.hero_cta_text, ar: e.target.value } })}
                required
              />
            </div>
          </div>
          <div>
            <Label>{t('content.homepage.heroCtaUrl') || 'رابط زر الدعوة'}</Label>
            <Input
              value={formData.hero_cta_url}
              onChange={(e) => setFormData({ ...formData, hero_cta_url: e.target.value })}
              placeholder="/dashboard"
              required
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>{t('content.homepage.sectionSubtitleEn') || 'عنوان القسم الفرعي (إنجليزي)'}</Label>
              <Textarea
                value={formData.section_subtitle.en}
                onChange={(e) => setFormData({ ...formData, section_subtitle: { ...formData.section_subtitle, en: e.target.value } })}
                rows={2}
                required
              />
            </div>
            <div>
              <Label>{t('content.homepage.sectionSubtitleAr') || 'عنوان القسم الفرعي (عربي)'}</Label>
              <Textarea
                value={formData.section_subtitle.ar}
                onChange={(e) => setFormData({ ...formData, section_subtitle: { ...formData.section_subtitle, ar: e.target.value } })}
                rows={2}
                required
              />
            </div>
          </div>
          <div className="flex gap-4 justify-end">
            <Button type="button" variant="outline" onClick={onClose}>
              {t('common.cancel') || 'إلغاء'}
            </Button>
            <Button type="submit">
              {t('common.save') || 'حفظ'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// Feature Dialog Component
const FeatureDialog = ({
  isOpen,
  onClose,
  feature,
  onCreate,
  onUpdate,
}: {
  isOpen: boolean;
  onClose: () => void;
  feature?: HomepageFeature | null;
  onCreate: (data: any) => void;
  onUpdate: (data: { id: number; data: any }) => void;
}) => {
  const { t } = useLanguage();
  const [formData, setFormData] = useState({
    order: 1,
    icon: '',
    title: { en: '', ar: '' },
    description: { en: '', ar: '' },
    is_active: true,
  });

  useEffect(() => {
    if (feature) {
      // Handle both TranslatedField and string formats
      const title = typeof feature.title === 'string' 
        ? { en: feature.title, ar: feature.title }
        : (feature.title || { en: '', ar: '' });
      const description = typeof feature.description === 'string'
        ? { en: feature.description, ar: feature.description }
        : (feature.description || { en: '', ar: '' });
      
      setFormData({
        order: feature.order,
        icon: feature.icon,
        title,
        description,
        is_active: feature.is_active,
      });
    } else {
      setFormData({
        order: 1,
        icon: '',
        title: { en: '', ar: '' },
        description: { en: '', ar: '' },
        is_active: true,
      });
    }
  }, [feature]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (feature) {
      onUpdate({ id: feature.id, data: formData });
    } else {
      onCreate(formData);
    }
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>
            {feature ? (t('content.feature.edit') || 'تعديل الميزة') : (t('content.feature.add') || 'إضافة ميزة')}
          </DialogTitle>
          <DialogDescription>
            {feature ? (t('content.feature.editDesc') || 'قم بتعديل بيانات الميزة') : (t('content.feature.addDesc') || 'قم بإضافة ميزة جديدة')}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label>{t('content.feature.order') || 'الترتيب'}</Label>
            <Input
              type="number"
              value={formData.order}
              onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) })}
              required
            />
          </div>
          <div>
            <Label>{t('content.feature.icon') || 'الأيقونة'}</Label>
            <IconPicker
              value={formData.icon}
              onChange={(iconName) => setFormData({ ...formData, icon: iconName })}
              placeholder={t('content.feature.icon') || 'الأيقونة'}
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>{t('content.feature.titleEn') || 'العنوان (إنجليزي)'}</Label>
              <Input
                value={formData.title.en}
                onChange={(e) => setFormData({ ...formData, title: { ...formData.title, en: e.target.value } })}
                required
              />
            </div>
            <div>
              <Label>{t('content.feature.titleAr') || 'العنوان (عربي)'}</Label>
              <Input
                value={formData.title.ar}
                onChange={(e) => setFormData({ ...formData, title: { ...formData.title, ar: e.target.value } })}
                required
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>{t('content.feature.descriptionEn') || 'الوصف (إنجليزي)'}</Label>
              <Textarea
                value={formData.description.en}
                onChange={(e) => setFormData({ ...formData, description: { ...formData.description, en: e.target.value } })}
                rows={3}
                required
              />
            </div>
            <div>
              <Label>{t('content.feature.descriptionAr') || 'الوصف (عربي)'}</Label>
              <Textarea
                value={formData.description.ar}
                onChange={(e) => setFormData({ ...formData, description: { ...formData.description, ar: e.target.value } })}
                rows={3}
                required
              />
            </div>
          </div>
          <div className="flex gap-4 justify-end">
            <Button type="button" variant="outline" onClick={onClose}>
              {t('common.cancel') || 'إلغاء'}
            </Button>
            <Button type="submit">
              {t('common.save') || 'حفظ'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// Statistic Dialog Component
const StatisticDialog = ({
  isOpen,
  onClose,
  statistic,
  onCreate,
  onUpdate,
}: {
  isOpen: boolean;
  onClose: () => void;
  statistic?: HomepageStatistic | null;
  onCreate: (data: any) => void;
  onUpdate: (data: { id: number; data: any }) => void;
}) => {
  const { t } = useLanguage();
  const [formData, setFormData] = useState({
    order: 1,
    number: '',
    label: { en: '', ar: '' },
    is_active: true,
  });

  useEffect(() => {
    if (statistic) {
      // Handle both TranslatedField and string formats
      const label = typeof statistic.label === 'string'
        ? { en: statistic.label, ar: statistic.label }
        : (statistic.label || { en: '', ar: '' });
      
      setFormData({
        order: statistic.order,
        number: statistic.number,
        label,
        is_active: statistic.is_active,
      });
    } else {
      setFormData({
        order: 1,
        number: '',
        label: { en: '', ar: '' },
        is_active: true,
      });
    }
  }, [statistic]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (statistic) {
      onUpdate({ id: statistic.id, data: formData });
    } else {
      onCreate(formData);
    }
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>
            {statistic ? (t('content.statistic.edit') || 'تعديل الإحصائية') : (t('content.statistic.add') || 'إضافة إحصائية')}
          </DialogTitle>
          <DialogDescription>
            {statistic ? (t('content.statistic.editDesc') || 'قم بتعديل بيانات الإحصائية') : (t('content.statistic.addDesc') || 'قم بإضافة إحصائية جديدة')}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label>{t('content.statistic.order') || 'الترتيب'}</Label>
            <Input
              type="number"
              value={formData.order}
              onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) })}
              required
            />
          </div>
          <div>
            <Label>{t('content.statistic.number') || 'الرقم'}</Label>
            <Input
              value={formData.number}
              onChange={(e) => setFormData({ ...formData, number: e.target.value })}
              placeholder="500+"
              required
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>{t('content.statistic.labelEn') || 'التسمية (إنجليزي)'}</Label>
              <Input
                value={formData.label.en}
                onChange={(e) => setFormData({ ...formData, label: { ...formData.label, en: e.target.value } })}
                required
              />
            </div>
            <div>
              <Label>{t('content.statistic.labelAr') || 'التسمية (عربي)'}</Label>
              <Input
                value={formData.label.ar}
                onChange={(e) => setFormData({ ...formData, label: { ...formData.label, ar: e.target.value } })}
                required
              />
            </div>
          </div>
          <div className="flex gap-4 justify-end">
            <Button type="button" variant="outline" onClick={onClose}>
              {t('common.cancel') || 'إلغاء'}
            </Button>
            <Button type="submit">
              {t('common.save') || 'حفظ'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// About Content Dialog Component
const AboutContentDialog = ({
  isOpen,
  onClose,
  content,
  onSubmit,
}: {
  isOpen: boolean;
  onClose: () => void;
  content?: any;
  onSubmit: (data: any) => void;
}) => {
  const { t } = useLanguage();
  const [formData, setFormData] = useState({
    hero_title: { en: '', ar: '' },
    hero_subtitle: { en: '', ar: '' },
    vision_title: { en: '', ar: '' },
    vision_content: { en: '', ar: '' },
    vision_icon: '',
    mission_title: { en: '', ar: '' },
    mission_content: { en: '', ar: '' },
    mission_icon: '',
    values_section_title: { en: '', ar: '' },
    is_active: true,
  });

  useEffect(() => {
    if (content) {
      const hero_title = typeof content.hero_title === 'string' ? { en: content.hero_title, ar: content.hero_title } : (content.hero_title || { en: '', ar: '' });
      const hero_subtitle = typeof content.hero_subtitle === 'string' ? { en: content.hero_subtitle, ar: content.hero_subtitle } : (content.hero_subtitle || { en: '', ar: '' });
      const vision_title = typeof content.vision_title === 'string' ? { en: content.vision_title, ar: content.vision_title } : (content.vision_title || { en: '', ar: '' });
      const vision_content = typeof content.vision_content === 'string' ? { en: content.vision_content, ar: content.vision_content } : (content.vision_content || { en: '', ar: '' });
      const mission_title = typeof content.mission_title === 'string' ? { en: content.mission_title, ar: content.mission_title } : (content.mission_title || { en: '', ar: '' });
      const mission_content = typeof content.mission_content === 'string' ? { en: content.mission_content, ar: content.mission_content } : (content.mission_content || { en: '', ar: '' });
      const values_section_title = typeof content.values_section_title === 'string' ? { en: content.values_section_title, ar: content.values_section_title } : (content.values_section_title || { en: '', ar: '' });
      
      setFormData({
        hero_title,
        hero_subtitle,
        vision_title,
        vision_content,
        vision_icon: content.vision_icon || '',
        mission_title,
        mission_content,
        mission_icon: content.mission_icon || '',
        values_section_title,
        is_active: content.is_active ?? true,
      });
    }
  }, [content]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(formData);
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t('content.about.edit') || 'تعديل محتوى صفحة من نحن'}</DialogTitle>
          <DialogDescription>
            {t('content.about.editDescription') || 'قم بتعديل محتوى صفحة من نحن'}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>{t('content.about.heroTitleEn') || 'عنوان الخلفية (إنجليزي)'}</Label>
              <Input value={formData.hero_title.en} onChange={(e) => setFormData({ ...formData, hero_title: { ...formData.hero_title, en: e.target.value } })} required />
            </div>
            <div>
              <Label>{t('content.about.heroTitleAr') || 'عنوان الخلفية (عربي)'}</Label>
              <Input value={formData.hero_title.ar} onChange={(e) => setFormData({ ...formData, hero_title: { ...formData.hero_title, ar: e.target.value } })} required />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>{t('content.about.heroSubtitleEn') || 'وصف الخلفية (إنجليزي)'}</Label>
              <Textarea value={formData.hero_subtitle.en} onChange={(e) => setFormData({ ...formData, hero_subtitle: { ...formData.hero_subtitle, en: e.target.value } })} rows={3} required />
            </div>
            <div>
              <Label>{t('content.about.heroSubtitleAr') || 'وصف الخلفية (عربي)'}</Label>
              <Textarea value={formData.hero_subtitle.ar} onChange={(e) => setFormData({ ...formData, hero_subtitle: { ...formData.hero_subtitle, ar: e.target.value } })} rows={3} required />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>{t('content.about.visionTitleEn') || 'عنوان الرؤية (إنجليزي)'}</Label>
              <Input value={formData.vision_title.en} onChange={(e) => setFormData({ ...formData, vision_title: { ...formData.vision_title, en: e.target.value } })} required />
            </div>
            <div>
              <Label>{t('content.about.visionTitleAr') || 'عنوان الرؤية (عربي)'}</Label>
              <Input value={formData.vision_title.ar} onChange={(e) => setFormData({ ...formData, vision_title: { ...formData.vision_title, ar: e.target.value } })} required />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>{t('content.about.visionContentEn') || 'محتوى الرؤية (إنجليزي)'}</Label>
              <Textarea value={formData.vision_content.en} onChange={(e) => setFormData({ ...formData, vision_content: { ...formData.vision_content, en: e.target.value } })} rows={3} required />
            </div>
            <div>
              <Label>{t('content.about.visionContentAr') || 'محتوى الرؤية (عربي)'}</Label>
              <Textarea value={formData.vision_content.ar} onChange={(e) => setFormData({ ...formData, vision_content: { ...formData.vision_content, ar: e.target.value } })} rows={3} required />
            </div>
          </div>
          <div>
            <Label>{t('content.about.visionIcon') || 'أيقونة الرؤية'}</Label>
            <IconPicker
              value={formData.vision_icon}
              onChange={(iconName) => setFormData({ ...formData, vision_icon: iconName })}
              placeholder={t('content.about.visionIcon') || 'أيقونة الرؤية'}
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>{t('content.about.missionTitleEn') || 'عنوان المهمة (إنجليزي)'}</Label>
              <Input value={formData.mission_title.en} onChange={(e) => setFormData({ ...formData, mission_title: { ...formData.mission_title, en: e.target.value } })} required />
            </div>
            <div>
              <Label>{t('content.about.missionTitleAr') || 'عنوان المهمة (عربي)'}</Label>
              <Input value={formData.mission_title.ar} onChange={(e) => setFormData({ ...formData, mission_title: { ...formData.mission_title, ar: e.target.value } })} required />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>{t('content.about.missionContentEn') || 'محتوى المهمة (إنجليزي)'}</Label>
              <Textarea value={formData.mission_content.en} onChange={(e) => setFormData({ ...formData, mission_content: { ...formData.mission_content, en: e.target.value } })} rows={3} required />
            </div>
            <div>
              <Label>{t('content.about.missionContentAr') || 'محتوى المهمة (عربي)'}</Label>
              <Textarea value={formData.mission_content.ar} onChange={(e) => setFormData({ ...formData, mission_content: { ...formData.mission_content, ar: e.target.value } })} rows={3} required />
            </div>
          </div>
          <div>
            <Label>{t('content.about.missionIcon') || 'أيقونة المهمة'}</Label>
            <IconPicker
              value={formData.mission_icon}
              onChange={(iconName) => setFormData({ ...formData, mission_icon: iconName })}
              placeholder={t('content.about.missionIcon') || 'أيقونة المهمة'}
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>{t('content.about.valuesSectionTitleEn') || 'عنوان قسم القيم (إنجليزي)'}</Label>
              <Input value={formData.values_section_title.en} onChange={(e) => setFormData({ ...formData, values_section_title: { ...formData.values_section_title, en: e.target.value } })} required />
            </div>
            <div>
              <Label>{t('content.about.valuesSectionTitleAr') || 'عنوان قسم القيم (عربي)'}</Label>
              <Input value={formData.values_section_title.ar} onChange={(e) => setFormData({ ...formData, values_section_title: { ...formData.values_section_title, ar: e.target.value } })} required />
            </div>
          </div>
          <div className="flex gap-4 justify-end">
            <Button type="button" variant="outline" onClick={onClose}>{t('common.cancel') || 'إلغاء'}</Button>
            <Button type="submit">{t('common.save') || 'حفظ'}</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// Value Dialog Component
const ValueDialog = ({
  isOpen,
  onClose,
  value,
  onCreate,
  onUpdate,
}: {
  isOpen: boolean;
  onClose: () => void;
  value?: AboutValue | null;
  onCreate: (data: any) => void;
  onUpdate: (data: { id: number; data: any }) => void;
}) => {
  const { t } = useLanguage();
  const [formData, setFormData] = useState({
    order: 1,
    icon: '',
    title: { en: '', ar: '' },
    content: { en: '', ar: '' },
    is_active: true,
  });

  useEffect(() => {
    if (value) {
      const title = typeof value.title === 'string' ? { en: value.title, ar: value.title } : (value.title || { en: '', ar: '' });
      const content = typeof value.content === 'string' ? { en: value.content, ar: value.content } : (value.content || { en: '', ar: '' });
      setFormData({ order: value.order, icon: value.icon, title, content, is_active: value.is_active });
    } else {
      setFormData({ order: 1, icon: '', title: { en: '', ar: '' }, content: { en: '', ar: '' }, is_active: true });
    }
  }, [value]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (value) {
      onUpdate({ id: value.id, data: formData });
    } else {
      onCreate(formData);
    }
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{value ? (t('content.value.edit') || 'تعديل القيمة') : (t('content.value.add') || 'إضافة قيمة')}</DialogTitle>
          <DialogDescription>
            {value ? (t('content.value.editDesc') || 'قم بتعديل بيانات القيمة') : (t('content.value.addDesc') || 'قم بإضافة قيمة جديدة')}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label>{t('content.value.order') || 'الترتيب'}</Label>
            <Input type="number" value={formData.order} onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) })} required />
          </div>
          <div>
            <Label>{t('content.value.icon') || 'الأيقونة'}</Label>
            <IconPicker
              value={formData.icon}
              onChange={(iconName) => setFormData({ ...formData, icon: iconName })}
              placeholder={t('content.value.icon') || 'الأيقونة'}
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>{t('content.value.titleEn') || 'العنوان (إنجليزي)'}</Label>
              <Input value={formData.title.en} onChange={(e) => setFormData({ ...formData, title: { ...formData.title, en: e.target.value } })} required />
            </div>
            <div>
              <Label>{t('content.value.titleAr') || 'العنوان (عربي)'}</Label>
              <Input value={formData.title.ar} onChange={(e) => setFormData({ ...formData, title: { ...formData.title, ar: e.target.value } })} required />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>{t('content.value.contentEn') || 'المحتوى (إنجليزي)'}</Label>
              <Textarea value={formData.content.en} onChange={(e) => setFormData({ ...formData, content: { ...formData.content, en: e.target.value } })} rows={3} required />
            </div>
            <div>
              <Label>{t('content.value.contentAr') || 'المحتوى (عربي)'}</Label>
              <Textarea value={formData.content.ar} onChange={(e) => setFormData({ ...formData, content: { ...formData.content, ar: e.target.value } })} rows={3} required />
            </div>
          </div>
          <div className="flex gap-4 justify-end">
            <Button type="button" variant="outline" onClick={onClose}>{t('common.cancel') || 'إلغاء'}</Button>
            <Button type="submit">{t('common.save') || 'حفظ'}</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// How It Works Content Dialog Component
const HowItWorksContentDialog = ({
  isOpen,
  onClose,
  content,
  onSubmit,
}: {
  isOpen: boolean;
  onClose: () => void;
  content?: any;
  onSubmit: (data: any) => void;
}) => {
  const { t } = useLanguage();
  const [formData, setFormData] = useState({
    hero_title: { en: '', ar: '' },
    hero_subtitle: { en: '', ar: '' },
    cta_button_text: { en: '', ar: '' },
    cta_button_url: '',
    is_active: true,
  });

  useEffect(() => {
    if (content) {
      const hero_title = typeof content.hero_title === 'string' ? { en: content.hero_title, ar: content.hero_title } : (content.hero_title || { en: '', ar: '' });
      const hero_subtitle = typeof content.hero_subtitle === 'string' ? { en: content.hero_subtitle, ar: content.hero_subtitle } : (content.hero_subtitle || { en: '', ar: '' });
      const cta_button_text = typeof content.cta_button_text === 'string' ? { en: content.cta_button_text, ar: content.cta_button_text } : (content.cta_button_text || { en: '', ar: '' });
      setFormData({ hero_title, hero_subtitle, cta_button_text, cta_button_url: content.cta_button_url || '', is_active: content.is_active ?? true });
    }
  }, [content]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(formData);
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t('content.howItWorks.edit') || 'تعديل محتوى صفحة كيف يعمل'}</DialogTitle>
          <DialogDescription>
            {t('content.howItWorks.editDescription') || 'قم بتعديل محتوى صفحة كيف يعمل'}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>{t('content.howItWorks.heroTitleEn') || 'عنوان الخلفية (إنجليزي)'}</Label>
              <Input value={formData.hero_title.en} onChange={(e) => setFormData({ ...formData, hero_title: { ...formData.hero_title, en: e.target.value } })} required />
            </div>
            <div>
              <Label>{t('content.howItWorks.heroTitleAr') || 'عنوان الخلفية (عربي)'}</Label>
              <Input value={formData.hero_title.ar} onChange={(e) => setFormData({ ...formData, hero_title: { ...formData.hero_title, ar: e.target.value } })} required />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>{t('content.howItWorks.heroSubtitleEn') || 'وصف الخلفية (إنجليزي)'}</Label>
              <Textarea value={formData.hero_subtitle.en} onChange={(e) => setFormData({ ...formData, hero_subtitle: { ...formData.hero_subtitle, en: e.target.value } })} rows={3} required />
            </div>
            <div>
              <Label>{t('content.howItWorks.heroSubtitleAr') || 'وصف الخلفية (عربي)'}</Label>
              <Textarea value={formData.hero_subtitle.ar} onChange={(e) => setFormData({ ...formData, hero_subtitle: { ...formData.hero_subtitle, ar: e.target.value } })} rows={3} required />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>{t('content.howItWorks.ctaButtonTextEn') || 'نص زر الدعوة (إنجليزي)'}</Label>
              <Input value={formData.cta_button_text.en} onChange={(e) => setFormData({ ...formData, cta_button_text: { ...formData.cta_button_text, en: e.target.value } })} required />
            </div>
            <div>
              <Label>{t('content.howItWorks.ctaButtonTextAr') || 'نص زر الدعوة (عربي)'}</Label>
              <Input value={formData.cta_button_text.ar} onChange={(e) => setFormData({ ...formData, cta_button_text: { ...formData.cta_button_text, ar: e.target.value } })} required />
            </div>
          </div>
          <div>
            <Label>{t('content.howItWorks.ctaButtonUrl') || 'رابط زر الدعوة'}</Label>
            <Input value={formData.cta_button_url} onChange={(e) => setFormData({ ...formData, cta_button_url: e.target.value })} required />
          </div>
          <div className="flex gap-4 justify-end">
            <Button type="button" variant="outline" onClick={onClose}>{t('common.cancel') || 'إلغاء'}</Button>
            <Button type="submit">{t('common.save') || 'حفظ'}</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// Step Dialog Component
const StepDialog = ({
  isOpen,
  onClose,
  step,
  onCreate,
  onUpdate,
}: {
  isOpen: boolean;
  onClose: () => void;
  step?: HowItWorksStep | null;
  onCreate: (data: any) => void;
  onUpdate: (data: { id: number; data: any }) => void;
}) => {
  const { t } = useLanguage();
  const [formData, setFormData] = useState({
    step_number: 1,
    icon: '',
    title: { en: '', ar: '' },
    description: { en: '', ar: '' },
    is_active: true,
  });

  useEffect(() => {
    if (step) {
      const title = typeof step.title === 'string' ? { en: step.title, ar: step.title } : (step.title || { en: '', ar: '' });
      const description = typeof step.description === 'string' ? { en: step.description, ar: step.description } : (step.description || { en: '', ar: '' });
      setFormData({ step_number: step.step_number, icon: step.icon, title, description, is_active: step.is_active });
    } else {
      setFormData({ step_number: 1, icon: '', title: { en: '', ar: '' }, description: { en: '', ar: '' }, is_active: true });
    }
  }, [step]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (step) {
      onUpdate({ id: step.id, data: formData });
    } else {
      onCreate(formData);
    }
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{step ? (t('content.step.edit') || 'تعديل الخطوة') : (t('content.step.add') || 'إضافة خطوة')}</DialogTitle>
          <DialogDescription>
            {step ? (t('content.step.editDesc') || 'قم بتعديل بيانات الخطوة') : (t('content.step.addDesc') || 'قم بإضافة خطوة جديدة')}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label>{t('content.step.stepNumber') || 'رقم الخطوة'}</Label>
            <Input type="number" value={formData.step_number} onChange={(e) => setFormData({ ...formData, step_number: parseInt(e.target.value) })} required />
          </div>
          <div>
            <Label>{t('content.step.icon') || 'الأيقونة'}</Label>
            <IconPicker
              value={formData.icon}
              onChange={(iconName) => setFormData({ ...formData, icon: iconName })}
              placeholder={t('content.step.icon') || 'الأيقونة'}
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>{t('content.step.titleEn') || 'العنوان (إنجليزي)'}</Label>
              <Input value={formData.title.en} onChange={(e) => setFormData({ ...formData, title: { ...formData.title, en: e.target.value } })} required />
            </div>
            <div>
              <Label>{t('content.step.titleAr') || 'العنوان (عربي)'}</Label>
              <Input value={formData.title.ar} onChange={(e) => setFormData({ ...formData, title: { ...formData.title, ar: e.target.value } })} required />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>{t('content.step.descriptionEn') || 'الوصف (إنجليزي)'}</Label>
              <Textarea value={formData.description.en} onChange={(e) => setFormData({ ...formData, description: { ...formData.description, en: e.target.value } })} rows={3} required />
            </div>
            <div>
              <Label>{t('content.step.descriptionAr') || 'الوصف (عربي)'}</Label>
              <Textarea value={formData.description.ar} onChange={(e) => setFormData({ ...formData, description: { ...formData.description, ar: e.target.value } })} rows={3} required />
            </div>
          </div>
          <div className="flex gap-4 justify-end">
            <Button type="button" variant="outline" onClick={onClose}>{t('common.cancel') || 'إلغاء'}</Button>
            <Button type="submit">{t('common.save') || 'حفظ'}</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// FAQ Dialog Component
const FAQDialog = ({
  isOpen,
  onClose,
  faq,
  onCreate,
  onUpdate,
}: {
  isOpen: boolean;
  onClose: () => void;
  faq?: FAQ | null;
  onCreate: (data: any) => void;
  onUpdate: (data: { id: number; data: any }) => void;
}) => {
  const { t } = useLanguage();
  const [formData, setFormData] = useState({
    order: 1,
    question: { en: '', ar: '' },
    answer: { en: '', ar: '' },
    is_active: true,
  });

  useEffect(() => {
    if (faq) {
      const question = typeof faq.question === 'string' ? { en: faq.question, ar: faq.question } : (faq.question || { en: '', ar: '' });
      const answer = typeof faq.answer === 'string' ? { en: faq.answer, ar: faq.answer } : (faq.answer || { en: '', ar: '' });
      setFormData({ order: faq.order, question, answer, is_active: faq.is_active });
    } else {
      setFormData({ order: 1, question: { en: '', ar: '' }, answer: { en: '', ar: '' }, is_active: true });
    }
  }, [faq]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (faq) {
      onUpdate({ id: faq.id, data: formData });
    } else {
      onCreate(formData);
    }
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{faq ? (t('content.faq.edit') || 'تعديل السؤال') : (t('content.faq.add') || 'إضافة سؤال')}</DialogTitle>
          <DialogDescription>
            {faq ? (t('content.faq.editDesc') || 'قم بتعديل بيانات السؤال') : (t('content.faq.addDesc') || 'قم بإضافة سؤال جديد')}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label>{t('content.faq.order') || 'الترتيب'}</Label>
            <Input type="number" value={formData.order} onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) })} required />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>{t('content.faq.questionEn') || 'السؤال (إنجليزي)'}</Label>
              <Input value={formData.question.en} onChange={(e) => setFormData({ ...formData, question: { ...formData.question, en: e.target.value } })} required />
            </div>
            <div>
              <Label>{t('content.faq.questionAr') || 'السؤال (عربي)'}</Label>
              <Input value={formData.question.ar} onChange={(e) => setFormData({ ...formData, question: { ...formData.question, ar: e.target.value } })} required />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>{t('content.faq.answerEn') || 'الإجابة (إنجليزي)'}</Label>
              <Textarea value={formData.answer.en} onChange={(e) => setFormData({ ...formData, answer: { ...formData.answer, en: e.target.value } })} rows={4} required />
            </div>
            <div>
              <Label>{t('content.faq.answerAr') || 'الإجابة (عربي)'}</Label>
              <Textarea value={formData.answer.ar} onChange={(e) => setFormData({ ...formData, answer: { ...formData.answer, ar: e.target.value } })} rows={4} required />
            </div>
          </div>
          <div className="flex gap-4 justify-end">
            <Button type="button" variant="outline" onClick={onClose}>{t('common.cancel') || 'إلغاء'}</Button>
            <Button type="submit">{t('common.save') || 'حفظ'}</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// Contact Content Dialog Component
const ContactContentDialog = ({
  isOpen,
  onClose,
  content,
  onSubmit,
}: {
  isOpen: boolean;
  onClose: () => void;
  content?: any;
  onSubmit: (data: any) => void;
}) => {
  const { t } = useLanguage();
  const [formData, setFormData] = useState({
    hero_title: { en: '', ar: '' },
    hero_subtitle: { en: '', ar: '' },
    form_title: { en: '', ar: '' },
    form_name_label: { en: '', ar: '' },
    form_email_label: { en: '', ar: '' },
    form_phone_label: { en: '', ar: '' },
    form_subject_label: { en: '', ar: '' },
    form_message_label: { en: '', ar: '' },
    form_submit_button_text: { en: '', ar: '' },
    phone: '',
    email: '',
    address: { en: '', ar: '' },
    phone_icon: '',
    email_icon: '',
    address_icon: '',
    is_active: true,
  });

  useEffect(() => {
    if (content) {
      const hero_title = typeof content.hero_title === 'string' ? { en: content.hero_title, ar: content.hero_title } : (content.hero_title || { en: '', ar: '' });
      const hero_subtitle = typeof content.hero_subtitle === 'string' ? { en: content.hero_subtitle, ar: content.hero_subtitle } : (content.hero_subtitle || { en: '', ar: '' });
      const form_title = typeof content.form_title === 'string' ? { en: content.form_title, ar: content.form_title } : (content.form_title || { en: '', ar: '' });
      const form_name_label = typeof content.form_name_label === 'string' ? { en: content.form_name_label, ar: content.form_name_label } : (content.form_name_label || { en: '', ar: '' });
      const form_email_label = typeof content.form_email_label === 'string' ? { en: content.form_email_label, ar: content.form_email_label } : (content.form_email_label || { en: '', ar: '' });
      const form_phone_label = typeof content.form_phone_label === 'string' ? { en: content.form_phone_label, ar: content.form_phone_label } : (content.form_phone_label || { en: '', ar: '' });
      const form_subject_label = typeof content.form_subject_label === 'string' ? { en: content.form_subject_label, ar: content.form_subject_label } : (content.form_subject_label || { en: '', ar: '' });
      const form_message_label = typeof content.form_message_label === 'string' ? { en: content.form_message_label, ar: content.form_message_label } : (content.form_message_label || { en: '', ar: '' });
      const form_submit_button_text = typeof content.form_submit_button_text === 'string' ? { en: content.form_submit_button_text, ar: content.form_submit_button_text } : (content.form_submit_button_text || { en: '', ar: '' });
      const address = typeof content.address === 'string' ? { en: content.address, ar: content.address } : (content.address || { en: '', ar: '' });
      setFormData({
        hero_title, hero_subtitle, form_title, form_name_label, form_email_label, form_phone_label,
        form_subject_label, form_message_label, form_submit_button_text, phone: content.phone || '', email: content.email || '',
        address, phone_icon: content.phone_icon || '', email_icon: content.email_icon || '', address_icon: content.address_icon || '',
        is_active: content.is_active ?? true,
      });
    } else {
      setFormData({
        hero_title: { en: '', ar: '' },
        hero_subtitle: { en: '', ar: '' },
        form_title: { en: '', ar: '' },
        form_name_label: { en: '', ar: '' },
        form_email_label: { en: '', ar: '' },
        form_phone_label: { en: '', ar: '' },
        form_subject_label: { en: '', ar: '' },
        form_message_label: { en: '', ar: '' },
        form_submit_button_text: { en: '', ar: '' },
        phone: '',
        email: '',
        address: { en: '', ar: '' },
        phone_icon: '',
        email_icon: '',
        address_icon: '',
        is_active: true,
      });
    }
  }, [content]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(formData);
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t('content.contact.edit') || 'تعديل محتوى صفحة التواصل'}</DialogTitle>
          <DialogDescription>
            {t('content.contact.editDescription') || 'قم بتعديل محتوى صفحة التواصل'}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>{t('content.contact.heroTitleEn') || 'عنوان الخلفية (إنجليزي)'}</Label>
              <Input value={formData.hero_title.en} onChange={(e) => setFormData({ ...formData, hero_title: { ...formData.hero_title, en: e.target.value } })} required />
            </div>
            <div>
              <Label>{t('content.contact.heroTitleAr') || 'عنوان الخلفية (عربي)'}</Label>
              <Input value={formData.hero_title.ar} onChange={(e) => setFormData({ ...formData, hero_title: { ...formData.hero_title, ar: e.target.value } })} required />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>{t('content.contact.heroSubtitleEn') || 'وصف الخلفية (إنجليزي)'}</Label>
              <Textarea value={formData.hero_subtitle.en} onChange={(e) => setFormData({ ...formData, hero_subtitle: { ...formData.hero_subtitle, en: e.target.value } })} rows={2} required />
            </div>
            <div>
              <Label>{t('content.contact.heroSubtitleAr') || 'وصف الخلفية (عربي)'}</Label>
              <Textarea value={formData.hero_subtitle.ar} onChange={(e) => setFormData({ ...formData, hero_subtitle: { ...formData.hero_subtitle, ar: e.target.value } })} rows={2} required />
            </div>
          </div>
          <div>
            <Label>{t('content.contact.phone') || 'الهاتف'}</Label>
            <Input value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} required />
          </div>
          <div>
            <Label>{t('content.contact.email') || 'البريد الإلكتروني'}</Label>
            <Input type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} required />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>{t('content.contact.addressEn') || 'العنوان (إنجليزي)'}</Label>
              <Input value={formData.address.en} onChange={(e) => setFormData({ ...formData, address: { ...formData.address, en: e.target.value } })} required />
            </div>
            <div>
              <Label>{t('content.contact.addressAr') || 'العنوان (عربي)'}</Label>
              <Input value={formData.address.ar} onChange={(e) => setFormData({ ...formData, address: { ...formData.address, ar: e.target.value } })} required />
            </div>
          </div>
          <div className="grid grid-cols-3 gap-4">
            <div>
              <Label>{t('content.contact.phoneIcon') || 'أيقونة الهاتف'}</Label>
              <IconPicker
                value={formData.phone_icon}
                onChange={(iconName) => setFormData({ ...formData, phone_icon: iconName })}
                placeholder={t('content.contact.phoneIcon') || 'أيقونة الهاتف'}
              />
            </div>
            <div>
              <Label>{t('content.contact.emailIcon') || 'أيقونة البريد'}</Label>
              <IconPicker
                value={formData.email_icon}
                onChange={(iconName) => setFormData({ ...formData, email_icon: iconName })}
                placeholder={t('content.contact.emailIcon') || 'أيقونة البريد'}
              />
            </div>
            <div>
              <Label>{t('content.contact.addressIcon') || 'أيقونة العنوان'}</Label>
              <IconPicker
                value={formData.address_icon}
                onChange={(iconName) => setFormData({ ...formData, address_icon: iconName })}
                placeholder={t('content.contact.addressIcon') || 'أيقونة العنوان'}
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>{t('content.contact.formTitleEn') || 'عنوان النموذج (إنجليزي)'}</Label>
              <Input value={formData.form_title.en} onChange={(e) => setFormData({ ...formData, form_title: { ...formData.form_title, en: e.target.value } })} required />
            </div>
            <div>
              <Label>{t('content.contact.formTitleAr') || 'عنوان النموذج (عربي)'}</Label>
              <Input value={formData.form_title.ar} onChange={(e) => setFormData({ ...formData, form_title: { ...formData.form_title, ar: e.target.value } })} required />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>{t('content.contact.formNameLabelEn') || 'تسمية الاسم (إنجليزي)'}</Label>
              <Input value={formData.form_name_label.en} onChange={(e) => setFormData({ ...formData, form_name_label: { ...formData.form_name_label, en: e.target.value } })} required />
            </div>
            <div>
              <Label>{t('content.contact.formNameLabelAr') || 'تسمية الاسم (عربي)'}</Label>
              <Input value={formData.form_name_label.ar} onChange={(e) => setFormData({ ...formData, form_name_label: { ...formData.form_name_label, ar: e.target.value } })} required />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>{t('content.contact.formEmailLabelEn') || 'تسمية البريد (إنجليزي)'}</Label>
              <Input value={formData.form_email_label.en} onChange={(e) => setFormData({ ...formData, form_email_label: { ...formData.form_email_label, en: e.target.value } })} required />
            </div>
            <div>
              <Label>{t('content.contact.formEmailLabelAr') || 'تسمية البريد (عربي)'}</Label>
              <Input value={formData.form_email_label.ar} onChange={(e) => setFormData({ ...formData, form_email_label: { ...formData.form_email_label, ar: e.target.value } })} required />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>{t('content.contact.formPhoneLabelEn') || 'تسمية الهاتف (إنجليزي)'}</Label>
              <Input value={formData.form_phone_label.en} onChange={(e) => setFormData({ ...formData, form_phone_label: { ...formData.form_phone_label, en: e.target.value } })} required />
            </div>
            <div>
              <Label>{t('content.contact.formPhoneLabelAr') || 'تسمية الهاتف (عربي)'}</Label>
              <Input value={formData.form_phone_label.ar} onChange={(e) => setFormData({ ...formData, form_phone_label: { ...formData.form_phone_label, ar: e.target.value } })} required />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>{t('content.contact.formSubjectLabelEn') || 'تسمية الموضوع (إنجليزي)'}</Label>
              <Input value={formData.form_subject_label.en} onChange={(e) => setFormData({ ...formData, form_subject_label: { ...formData.form_subject_label, en: e.target.value } })} required />
            </div>
            <div>
              <Label>{t('content.contact.formSubjectLabelAr') || 'تسمية الموضوع (عربي)'}</Label>
              <Input value={formData.form_subject_label.ar} onChange={(e) => setFormData({ ...formData, form_subject_label: { ...formData.form_subject_label, ar: e.target.value } })} required />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>{t('content.contact.formMessageLabelEn') || 'تسمية الرسالة (إنجليزي)'}</Label>
              <Input value={formData.form_message_label.en} onChange={(e) => setFormData({ ...formData, form_message_label: { ...formData.form_message_label, en: e.target.value } })} required />
            </div>
            <div>
              <Label>{t('content.contact.formMessageLabelAr') || 'تسمية الرسالة (عربي)'}</Label>
              <Input value={formData.form_message_label.ar} onChange={(e) => setFormData({ ...formData, form_message_label: { ...formData.form_message_label, ar: e.target.value } })} required />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>{t('content.contact.formSubmitButtonTextEn') || 'نص زر الإرسال (إنجليزي)'}</Label>
              <Input value={formData.form_submit_button_text.en} onChange={(e) => setFormData({ ...formData, form_submit_button_text: { ...formData.form_submit_button_text, en: e.target.value } })} required />
            </div>
            <div>
              <Label>{t('content.contact.formSubmitButtonTextAr') || 'نص زر الإرسال (عربي)'}</Label>
              <Input value={formData.form_submit_button_text.ar} onChange={(e) => setFormData({ ...formData, form_submit_button_text: { ...formData.form_submit_button_text, ar: e.target.value } })} required />
            </div>
          </div>
          <div className="flex gap-4 justify-end">
            <Button type="button" variant="outline" onClick={onClose}>{t('common.cancel') || 'إلغاء'}</Button>
            <Button type="submit">{t('common.save') || 'حفظ'}</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// Navigation Link Dialog Component
const NavigationLinkDialog = ({
  isOpen,
  onClose,
  link,
  onCreate,
  onUpdate,
}: {
  isOpen: boolean;
  onClose: () => void;
  link?: NavigationLink | null;
  onCreate: (data: any) => void;
  onUpdate: (data: { id: number; data: any }) => void;
}) => {
  const { t } = useLanguage();
  const [formData, setFormData] = useState({
    order: 1,
    label: { en: '', ar: '' },
    url: '',
    route_name: '',
    is_external: false,
    is_active: true,
  });

  useEffect(() => {
    if (link) {
      const label = typeof link.label === 'string' ? { en: link.label, ar: link.label } : (link.label || { en: '', ar: '' });
      setFormData({ order: link.order, label, url: link.url, route_name: link.route_name || '', is_external: link.is_external, is_active: link.is_active });
    } else {
      setFormData({ order: 1, label: { en: '', ar: '' }, url: '', route_name: '', is_external: false, is_active: true });
    }
  }, [link]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (link) {
      onUpdate({ id: link.id, data: formData });
    } else {
      onCreate(formData);
    }
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{link ? (t('content.navigation.edit') || 'تعديل الرابط') : (t('content.navigation.add') || 'إضافة رابط')}</DialogTitle>
          <DialogDescription>
            {link ? (t('content.navigation.editDesc') || 'قم بتعديل بيانات الرابط') : (t('content.navigation.addDesc') || 'قم بإضافة رابط جديد')}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label>{t('content.navigation.order') || 'الترتيب'}</Label>
            <Input type="number" value={formData.order} onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) })} required />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>{t('content.navigation.labelEn') || 'التسمية (إنجليزي)'}</Label>
              <Input value={formData.label.en} onChange={(e) => setFormData({ ...formData, label: { ...formData.label, en: e.target.value } })} required />
            </div>
            <div>
              <Label>{t('content.navigation.labelAr') || 'التسمية (عربي)'}</Label>
              <Input value={formData.label.ar} onChange={(e) => setFormData({ ...formData, label: { ...formData.label, ar: e.target.value } })} required />
            </div>
          </div>
          <div>
            <Label>{t('content.navigation.url') || 'الرابط'}</Label>
            <Input value={formData.url} onChange={(e) => setFormData({ ...formData, url: e.target.value })} required />
          </div>
          <div>
            <Label>{t('content.navigation.routeName') || 'اسم المسار'}</Label>
            <Input value={formData.route_name} onChange={(e) => setFormData({ ...formData, route_name: e.target.value })} />
          </div>
          <div className="flex gap-4 justify-end">
            <Button type="button" variant="outline" onClick={onClose}>{t('common.cancel') || 'إلغاء'}</Button>
            <Button type="submit">{t('common.save') || 'حفظ'}</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// Footer Content Dialog Component
const FooterContentDialog = ({
  isOpen,
  onClose,
  content,
  onSubmit,
}: {
  isOpen: boolean;
  onClose: () => void;
  content?: any;
  onSubmit: (data: any) => void;
}) => {
  const { t } = useLanguage();
  const [formData, setFormData] = useState({
    company_description: { en: '', ar: '' },
    company_logo: '',
    quick_links_title: { en: '', ar: '' },
    legal_title: { en: '', ar: '' },
    contact_title: { en: '', ar: '' },
    phone: '',
    email: '',
    location: { en: '', ar: '' },
    copyright_text: '',
    is_active: true,
  });

  useEffect(() => {
    if (content) {
      const company_description = typeof content.company_description === 'string' ? { en: content.company_description, ar: content.company_description } : (content.company_description || { en: '', ar: '' });
      const quick_links_title = typeof content.quick_links_title === 'string' ? { en: content.quick_links_title, ar: content.quick_links_title } : (content.quick_links_title || { en: '', ar: '' });
      const legal_title = typeof content.legal_title === 'string' ? { en: content.legal_title, ar: content.legal_title } : (content.legal_title || { en: '', ar: '' });
      const contact_title = typeof content.contact_title === 'string' ? { en: content.contact_title, ar: content.contact_title } : (content.contact_title || { en: '', ar: '' });
      const location = typeof content.location === 'string' ? { en: content.location, ar: content.location } : (content.location || { en: '', ar: '' });
      setFormData({
        company_description, company_logo: content.company_logo || '', quick_links_title, legal_title, contact_title,
        phone: content.phone || '', email: content.email || '', location, copyright_text: content.copyright_text || '',
        is_active: content.is_active ?? true,
      });
    } else {
      setFormData({
        company_description: { en: '', ar: '' },
        company_logo: '',
        quick_links_title: { en: '', ar: '' },
        legal_title: { en: '', ar: '' },
        contact_title: { en: '', ar: '' },
        phone: '',
        email: '',
        location: { en: '', ar: '' },
        copyright_text: '',
        is_active: true,
      });
    }
  }, [content]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(formData);
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t('content.footer.edit') || 'تعديل محتوى التذييل'}</DialogTitle>
          <DialogDescription>
            {t('content.footer.editDescription') || 'قم بتعديل محتوى التذييل'}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>{t('content.footer.companyDescriptionEn') || 'وصف الشركة (إنجليزي)'}</Label>
              <Textarea value={formData.company_description.en} onChange={(e) => setFormData({ ...formData, company_description: { ...formData.company_description, en: e.target.value } })} rows={3} required />
            </div>
            <div>
              <Label>{t('content.footer.companyDescriptionAr') || 'وصف الشركة (عربي)'}</Label>
              <Textarea value={formData.company_description.ar} onChange={(e) => setFormData({ ...formData, company_description: { ...formData.company_description, ar: e.target.value } })} rows={3} required />
            </div>
          </div>
          <div>
            <Label>{t('content.footer.phone') || 'الهاتف'}</Label>
            <Input value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} required />
          </div>
          <div>
            <Label>{t('content.footer.email') || 'البريد الإلكتروني'}</Label>
            <Input type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} required />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>{t('content.footer.quickLinksTitleEn') || 'عنوان الروابط السريعة (إنجليزي)'}</Label>
              <Input value={formData.quick_links_title.en} onChange={(e) => setFormData({ ...formData, quick_links_title: { ...formData.quick_links_title, en: e.target.value } })} required />
            </div>
            <div>
              <Label>{t('content.footer.quickLinksTitleAr') || 'عنوان الروابط السريعة (عربي)'}</Label>
              <Input value={formData.quick_links_title.ar} onChange={(e) => setFormData({ ...formData, quick_links_title: { ...formData.quick_links_title, ar: e.target.value } })} required />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>{t('content.footer.legalTitleEn') || 'عنوان القانوني (إنجليزي)'}</Label>
              <Input value={formData.legal_title.en} onChange={(e) => setFormData({ ...formData, legal_title: { ...formData.legal_title, en: e.target.value } })} required />
            </div>
            <div>
              <Label>{t('content.footer.legalTitleAr') || 'عنوان القانوني (عربي)'}</Label>
              <Input value={formData.legal_title.ar} onChange={(e) => setFormData({ ...formData, legal_title: { ...formData.legal_title, ar: e.target.value } })} required />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>{t('content.footer.contactTitleEn') || 'عنوان التواصل (إنجليزي)'}</Label>
              <Input value={formData.contact_title.en} onChange={(e) => setFormData({ ...formData, contact_title: { ...formData.contact_title, en: e.target.value } })} required />
            </div>
            <div>
              <Label>{t('content.footer.contactTitleAr') || 'عنوان التواصل (عربي)'}</Label>
              <Input value={formData.contact_title.ar} onChange={(e) => setFormData({ ...formData, contact_title: { ...formData.contact_title, ar: e.target.value } })} required />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>{t('content.footer.locationEn') || 'الموقع (إنجليزي)'}</Label>
              <Input value={formData.location.en} onChange={(e) => setFormData({ ...formData, location: { ...formData.location, en: e.target.value } })} required />
            </div>
            <div>
              <Label>{t('content.footer.locationAr') || 'الموقع (عربي)'}</Label>
              <Input value={formData.location.ar} onChange={(e) => setFormData({ ...formData, location: { ...formData.location, ar: e.target.value } })} required />
            </div>
          </div>
          <div>
            <Label>{t('content.footer.copyrightText') || 'نص حقوق النشر'}</Label>
            <Input value={formData.copyright_text} onChange={(e) => setFormData({ ...formData, copyright_text: e.target.value })} required />
          </div>
          <div className="flex gap-4 justify-end">
            <Button type="button" variant="outline" onClick={onClose}>{t('common.cancel') || 'إلغاء'}</Button>
            <Button type="submit">{t('common.save') || 'حفظ'}</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default AdminContentManagement;
